// SPDX-License-Identifier: MIT
import type { Session } from '@supabase/supabase-js';
import { renderHeader } from '../components/header';
// renderWelcomeSidebar removed v7 — no sidebar on the new /welcome 2-col layout.
import { isSetupComplete } from '../lib/setup-state';
import { renderForgejoNextSteps } from '../components/forgejo-next-steps';
// getUserAiProvider import removed 2026-05-28 — onSubmit no longer short-circuits
// on local provider lookup ; the magnify edge fn picks user-key OR server gift-key
// internally, falling back via 403 ai_not_configured if neither is available.
import type { ComposerOutcome } from '../components/composer';
import { renderDiscoveryChat, type DiscoveryChatElement, type ChatTurnResult } from '../components/discovery-chat';
import { createGuidedDiscussion, type DiscussionState } from '../projects/idea-discussion';
import { renderDepositBox } from '../components/deposit-box';
import { isGuidedFirstRun, isGuidanceDismissed } from '../onboarding/first-run-state';
import { applyGuidedChrome } from '../onboarding/guided-chrome';
import { renderProjectPreviewCard } from '../components/project-preview-card';
// renderMyProjects : moved to the welcome-sidebar (2026-05-28 app-shell).
// renderFreemiumCounter + renderUpsellCard + renderCloudSyncButton : the
// counter + upsell + cloud-sync affordances moved to the welcome-sidebar
// account block (2026-05-28 app-shell). The desktop cloud-sync button
// itself is a future addition to the sidebar — out of scope for this
// redesign, restorable as a follow-up (the runCloudSync API is still
// imported below so the platform module stays exercised).
import { renderOnlinePreview, slugify } from '../components/online-preview';
import { shortHandle, handleFromDeployUrl } from '../projects/handle';
import { makeRedeployHandler } from '../projects/redeploy';
import { cloudEnabled } from '../checkout/cloud';
import { renderTerminalHandoff } from '../components/terminal-handoff';
import { renderProjectSection } from '../components/project-section';
// 2.7.1 item #2 — the delivery history col re-offers the backend provision CTA
// through the same wizard as the card backend slot (mirrors wireEmptyHandlers).
import {
  renderMiseEnLigneWizard,
  type BaseModel,
  type BackendModel,
  type DeployOutcome,
  type MiseEnLigneWizardOptions,
} from '../components/mise-en-ligne-wizard';
import { deployFrontendEnabled } from '../config/deploy';
import { renderFooter } from '../components/footer';
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import type { Locale } from '../i18n/detect';
import { getAiBridge, getDataBridge, detectPlatform, getUsageBridge, usageConsentGranted, grantUsageConsent, usageThrottleRepeated, getAttachmentBridge } from '../platform';
import { getAppVersion } from '../platform/app-version';
// getLatestPublishedVersion is unused in v7 (sidebar gone). getAppVersion is back:
// it feeds the desktop diagnostic line (see updateDesktopDiag below).
import { buildDeliveryFiles, deliverProject, cloneProject, adoptProjectFolder, openTerminal, writeProjectEnv, pushProjectCode, type AgentChannelMsg } from '../platform/desktop-delivery';
import { mintProjectWriteToken } from '../projects/code-push';
import { runCloudSave, type CloudSaveState } from '../projects/cloud-save';
import { renderCloudSaveIndicator } from '../components/cloud-save-indicator';
import { agentRunManager } from '../platform/agent-run-manager';
import { buildAgentBuildTask } from '../platform/agent-build-task';
import { loadSessions, saveSessions } from '../platform/session-store';
import { decideRun, type Session as AgentSession } from '../platform/session-policy';
import { claudeAuthStatus } from '../components/desktop-connect';
import { parseClaudeStreamLine } from '../platform/claude-stream';
import { renderAgentPanel } from '../components/agent-panel';
import { renderSessionHistory } from '../components/session-history';
import { renderInkGauge, renderInkGaugeCard, renderConsentLine, renderInkGaugeAwaiting, gaugeHasSignal } from '../components/ink-gauge';
import { syncDesktopProjectsOnLoad, pullDesktopFromCloud, pushSessionsForProject } from '../platform/run-cloud-sync';
import { setLocalDir } from '../platform/desktop-data-bridge';
import { loadSyncMap } from '../platform/syncmap-store';
import { getLastSyncedAt } from '../platform/sync-meta-store';
import { syncStatusFor } from '../platform/sync-status';
import { renderSyncBadge } from '../components/sync-badge';
import { saveGenesisForProject } from '../projects/save-genesis';
import { writeGenesisToDir, readGenesisFromDir } from '../platform/genesis-dir';
import { realHydrationStores } from '../platform/real-hydration-stores';
import { resumeOnThisMachine, type ResumeResult } from '../platform/resume-on-this-machine';
import { renderResumeProgress, renderResumePanel } from '../components/continuity-badge';
import { listVersionsForProject } from '../platform/version-store';
import { supabase } from '../auth/supabase';
import { previewDaysLeft, FREE_PREVIEW_DAYS } from '../lib/freemium';
import {
  projectRowToMagnifyResult,
  projectVersionToMagnifyResult,
  hasStoredDiscovery,
  type ProjectRow,
  type ProjectVersionRow,
  type VersionContextKind,
} from '../projects/api';
import { buildContextString, getProjectContext } from '../projects/attachments';
import { bootstrapForgejoRepo, deriveForgejoUrls } from '../projects/forgejo';
import { buildRefinementContext } from '../projects/discover';
import type { MagnifyResult } from '../auth/magnify';
import { getEntitlement, type Entitlement } from '../entitlements/api';
import { isQuotaError } from '../lib/quota-error';

// Per-project "first run since the webview loaded" marker. Reset on reload (= app
// open), so the FIRST build of a project in a fresh app session starts a NEW agent
// session (no --continue), per the session-boundary policy.
const appRunStarted = new Set<string>();
// Single shared timer for the desktop plan-usage gauge refresh — anti-leak across
// renderWelcomePage re-mounts on navigation (each render clears the prior one).
let usageGaugeTimer: ReturnType<typeof setInterval> | null = null;

// 2.7.1 item C — session bookkeeping shared ACROSS panel mounts, keyed by
// projectDir. The run manager's per-run capture closures (registered at
// startRun) append into these same objects, so a panel that remounts after
// navigation reads an up-to-date in-memory history without waiting for the
// debounced store flush. ONE array identity per projectDir forever — store
// loads splice in place so live closures stay coherent.
const agentSessionsCache = new Map<string, AgentSession[]>();
// The LAST mounted panel's history-accordion refresher per projectDir. The
// manager's onPersist calls whichever is current, so a background run keeps
// the visible history fresh; a stale (detached) refresher is simply
// overwritten by the next mount — rendering into a detached node is harmless.
const agentHistoryRefreshers = new Map<string, () => void>();
// Review fix C1 — deterministic display teardown, keyed by projectDir. Stores
// the PREVIOUS mount's detach (unsubscribe + timer stop); the next
// mountAgentPanel for the same dir calls it (last-mount-wins, same pattern as
// agentHistoryRefreshers). Without it, a panel detached BEFORE receiving any
// live message while connected (the common first-open double-render) never
// trips the isConnected latch and keeps parsing + DOM-rendering every line of
// every future run into a detached tree.
const agentPanelTeardowns = new Map<string, () => void>();
// Review fix I1 — the per-project cold-load of stored sessions, keyed by
// PROMISE so every mount awaits the SAME store read (a warm/cold boolean let
// mount #2 skip the load while mount #1 — possibly already detached — was
// still awaiting it → visibly empty panel + accordion). Deleted on rejection
// so the next mount retries instead of caching empty forever.
const agentSessionsLoads = new Map<string, Promise<void>>();

export interface WelcomePageOptions {
  session: Session;
  locale: Locale;
  onSignOut: () => void;
}

export function renderWelcomePage(opts: WelcomePageOptions): DocumentFragment {
  const frag = document.createDocumentFragment();
  const email = opts.session.user.email ?? '';
  const displayName = deriveDisplayName(opts.session);
  const initial = displayName.charAt(0).toUpperCase();

  // 2026-05-29 v7 : NO sidebar. Two LARGE equal columns for the idea
  // CREATION flow — operator brief : "2 LARGES colonnes POUR LA CRÉATION
  // DE L'IDÉE". LEFT col = INPUT (intro + composer + tes projets list at
  // bottom) ; RIGHT col = INTERPRETATION (placeholder when idle ; chat
  // input AT TOP + attachments + fiche + découverte when a project is
  // active). Livraison strip = full-width below both cols.
  // Header is a full-bleed sibling of .welcome-page (NOT a child), so it spans
  // the full viewport width like every other page. .welcome-page's max-width:1200
  // then only constrains the content below it. (Nested inside .welcome-page the
  // header was inset to ~1144px — the "menu pas en pleine largeur sur welcome"
  // report, 2026-05-30.)
  frag.appendChild(renderHeader({
    locale: opts.locale,
    user: { email, initial },
    onSignOut: opts.onSignOut,
    activePath: '/welcome',
  }));

  const page = document.createElement('div');
  page.className = 'welcome-page';
  page.setAttribute('data-component', 'welcome-page');
  frag.appendChild(page);

  let projectsCache_ext: ProjectRow[] = [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let renderIdleRef: () => void = () => {}; // wired by renderProjectsRegion.setRenderIdle
  let openProjectInCanvasRef: (row: ProjectRow) => void = () => {};
  void renderIdleRef; // referenced via the closure below

  const configured = isSetupComplete();

  const shell = document.createElement('div');
  shell.className = 'welcome-shell';
  shell.setAttribute('data-component', 'welcome-shell');
  page.appendChild(shell);

  const canvasLeft = document.createElement('main');
  canvasLeft.className = 'welcome-canvas-left';
  canvasLeft.setAttribute('data-component', 'welcome-canvas-left');
  shell.appendChild(canvasLeft);

  const canvasRight = document.createElement('section');
  canvasRight.className = 'welcome-canvas-right';
  canvasRight.setAttribute('data-component', 'welcome-canvas-right');
  shell.appendChild(canvasRight);

  // Delivery strip (full-width below both cols) — visible only when a
  // project is active (its Forgejo/online/desktop blocks get mounted here
  // instead of the project card body, per operator brief).
  const deliveryStrip = document.createElement('section');
  deliveryStrip.className = 'welcome-delivery-strip';
  deliveryStrip.setAttribute('data-component', 'welcome-delivery-strip');
  shell.appendChild(deliveryStrip);

  // Chemise d'atelier (zone-projet-v2) — full-width cover band ABOVE the two
  // columns in active mode. The card builds [data-component="project-cover"]
  // under its root ; renderEnrichedCardForRow moves it here post-render (same
  // travel pattern as the discovery node). Hidden in hero mode via CSS.
  const coverStrip = document.createElement('div');
  coverStrip.className = 'welcome-cover-strip';
  coverStrip.setAttribute('data-component', 'welcome-cover-strip');
  shell.insertBefore(coverStrip, canvasLeft);

  // Chat-first entry band (operator brief 2026-06-21) — a full-width row at the
  // TOP of the shell. As a direct shell child it is immune to the composer-host
  // hide rule + the canvasLeft fiche-swap, so the conversation stays pinned at
  // the top across the hero → active transition. renderProjectsRegion owns the
  // magnify machinery, so it mounts the chat here via the chatBandHost option.
  const chatBand = document.createElement('div');
  chatBand.className = 'welcome-chat-band';
  chatBand.setAttribute('data-component', 'welcome-chat-band');
  shell.insertBefore(chatBand, shell.firstChild);

  // ─── LEFT col content : intro + (composer mounted by renderProjectsRegion) + tes projets ─
  const titleRaw = t('welcome.slim.intro');
  const titleHtml = titleRaw.replace(
    /(idée|idea)/i,
    (m) => `<em>${escapeHtml(m)}</em>`,
  ) || `<em>${escapeHtml(titleRaw)}</em>`;
  const subRaw = t('welcome.slim.sub');
  const subHtml = subRaw.replace(
    /(ton Claude|your Claude)/i,
    (m) => `<em>${escapeHtml(m)}</em>`,
  );
  // 2.7.1 item A — « Mes projets » is the TOP pre-selection surface : the
  // picker strip mounts FIRST in the left canvas (above the intro + composer)
  // and stays there in active mode as a persistent switcher. Newcomers
  // (0 projects) keep the pure hero — the block carries .is-empty (CSS-hidden).
  const projectsBlock = document.createElement('div');
  projectsBlock.className = 'welcome-canvas-left__projects';
  projectsBlock.setAttribute('data-component', 'canvas-left-projects');
  const projectsHeading = document.createElement('h3');
  projectsHeading.className = 'welcome-canvas-left__projects-heading';
  projectsHeading.textContent = t('welcome.sidebar.myProjects');
  projectsBlock.appendChild(projectsHeading);
  const projectsList = document.createElement('div');
  projectsList.className = 'welcome-canvas-left__projects-grid';
  projectsList.setAttribute('data-component', 'canvas-left-project-list');
  projectsBlock.appendChild(projectsList);
  canvasLeft.appendChild(projectsBlock);

  const intro = document.createElement('div');
  intro.className = 'welcome-canvas__intro';
  intro.innerHTML = `
    <p class="welcome-canvas__kicker">${escapeHtml(t('welcome.slim.kicker'))}</p>
    <h1 class="welcome-canvas__title">${titleHtml}</h1>
    <p class="welcome-canvas__sub">${subHtml}</p>`;
  canvasLeft.appendChild(intro);

  const renderProjectsList = (): void => {
    projectsList.innerHTML = '';
    projectsBlock.classList.toggle('is-empty', projectsCache_ext.length === 0);
    if (projectsCache_ext.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'welcome-canvas-left__empty';
      empty.textContent = t('welcome.sidebar.emptyProjects');
      projectsList.appendChild(empty);
      return;
    }
    for (const row of projectsCache_ext) {
      const btn = buildProjectPickerTile(row, opts.locale);
      btn.addEventListener('click', () => openProjectInCanvasRef(row));
      projectsList.appendChild(btn);
    }
  };

  // ─── RIGHT col content : activeSlot ONLY (the col itself is display:none
  // when shell has no .is-active class — single-col hero state) ─
  const activeSlot = document.createElement('div');
  activeSlot.className = 'welcome-canvas-right__active';
  activeSlot.setAttribute('data-component', 'canvas-right-active');
  canvasRight.appendChild(activeSlot);

  // 2026-05-29 v7.1 : adaptive layout. Shell starts WITHOUT .is-active →
  // single col, full-width magnify hero, right col hidden. On interpretation
  // (magnify success OR open past project), add .is-active → 2-col split +
  // right col visible + delivery strip visible.
  const showHero = (): void => {
    shell.classList.remove('is-active');
    activeSlot.innerHTML = '';
    deliveryStrip.classList.remove('is-visible');
    deliveryStrip.innerHTML = '';
    coverStrip.innerHTML = '';
    for (const el of projectsList.querySelectorAll<HTMLElement>('.is-active')) el.classList.remove('is-active');
  };
  const showInterpretation = (): void => {
    shell.classList.add('is-active');
    activeSlot.innerHTML = '';
    deliveryStrip.classList.add('is-visible');
    deliveryStrip.innerHTML = '';
    coverStrip.innerHTML = '';
  };

  openProjectInCanvasRef = (row) => {
    showInterpretation();
    // The chat band now serves an OPENED project too (operator 2026-06-22):
    // mount a converse-bound refine chat in it (no discovery phase, no Magnifier
    // — the idea is already magnified). The band is a direct shell child, immune
    // to the canvasLeft fiche-swap, so it never disappears. A fresh mount each
    // open re-binds the chat to this row (and clears any prior thread).
    (canvasRight as unknown as { __mountConverseChat?: (r: ProjectRow) => void })
      .__mountConverseChat?.(row);
    // v7.8 — LEFT = the renderProjectPreviewCard collapsible card (✦ ta fiche
    // with built-in expand/collapse + 4 actions Régénérer · Copier ·
    // Dossier · Starter Git). RIGHT = chat + attachments + discovery —
    // the card's sub-blocks are routed there via the host options
    // (chatHost / attachmentsHost / discoveryHost) set on renderProjectsRegion.
    // Clearing activeLeftFiche AND activeSlot before mount prevents the
    // 2-bar bug from v7.6 (saveProject re-fired renderEnrichedCardForRow
    // and chat appended without clearing).
    activeLeftFiche.innerHTML = '';
    activeSlot.innerHTML = '';
    const card = (canvasRight as unknown as { __openRow?: (r: ProjectRow) => HTMLElement }).__openRow?.(row);
    if (card) {
      activeLeftFiche.appendChild(card);
      placeBackButton();
      // 2.7.1 item B — an already-delivered desktop project opens all-collapsed
      // (fiche + plan + files + discovery folded); the actionable surface is the
      // delivery strip below, so bring it into view once the frame settles
      // (same rAF pattern as scrollCardIntoView in the projects region).
      if (detectPlatform() === 'desktop' && row.local_dir) {
        requestAnimationFrame(() => {
          deliveryStrip.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
    for (const el of projectsList.querySelectorAll<HTMLElement>('[data-component="canvas-left-project-item"]')) {
      el.classList.toggle('is-active', el.dataset.projectId === row.id);
    }
  };

  // Render the work region. COMPOSER mounts in canvasLeft right after the
  // intro (the picker strip sits ABOVE the intro since 2.7.1 item A).
  const composerHost = document.createElement('div');
  composerHost.setAttribute('data-component', 'composer-host');
  canvasLeft.appendChild(composerHost);

  // v7.2 : in active state, the magnify box (composerHost above) is HIDDEN
  // via CSS and this fiche slot is SHOWN in its place. Operator brief :
  // "la box magnifiée est remplacée par ton projet magnifié (colonne de
  // gauche)". The slot is populated by openProjectInCanvasRef + the magnify
  // success path.
  const activeLeftFiche = document.createElement('div');
  activeLeftFiche.setAttribute('data-component', 'canvas-left-active-fiche');
  canvasLeft.appendChild(activeLeftFiche);

  // v7.8 (2026-05-29) — operator validated answers :
  // Q1 = yes use the existing renderProjectPreviewCard collapsible in LEFT
  // Q2 = the "2 bars" was 2 chat bars (saveProject → renderSaved re-fired
  //      renderEnrichedCardForRow which created another chatWrap without
  //      clearing the chatHost)
  // Q3 = yes RIGHT col = chat + attachments + discovery, ONCE each.
  //
  // No buildLeftFiche custom — the renderProjectPreviewCard component IS
  // the ✦ ta fiche pinned card with proper expand/collapse + actions, since
  // SP-2. We just route its sub-blocks (chat/attachments/discovery) to the
  // RIGHT col via host options, while the card body lands in LEFT.
  const placeBackButton = (): void => {
    // Chemise mode : « ← Nouvelle idée » belongs to the cover toolbar's back
    // slot (one uniform actions band) ; fallback to the legacy left-col spot
    // when no cover is mounted (e.g. fresh-magnify path without a row yet).
    const coverSlot = coverStrip.querySelector<HTMLElement>('[data-component="cover-back-slot"]');
    const host = coverSlot ?? activeLeftFiche;
    if (host.querySelector('[data-component="left-back-to-new-idea"]')) return;
    // Fresh-magnify path: a legacy back may already sit in the left col from
    // BEFORE the cover mounted — never show two.
    if (coverSlot) activeLeftFiche.querySelector('[data-component="left-back-to-new-idea"]')?.remove();
    const back = document.createElement('button');
    back.type = 'button';
    back.className = coverSlot
      ? 'project-cover__back'
      : 'welcome-canvas-left__active-fiche__back welcome-canvas-left__active-fiche__back--bottom';
    back.setAttribute('data-component', 'left-back-to-new-idea');
    back.textContent = `← ${t('welcome.sidebar.newIdea').replace('+ ', '')}`;
    back.addEventListener('click', () => renderIdleRef());
    host.appendChild(back);
  };

  // 2026-06-02 : desktop diagnostic line — running version + local-store summary.
  // The v7 sidebar removal left NO on-screen way to tell which build is actually
  // installed (the recurring "is the old binary still running?" trap from the
  // 2026-05-30 blind-build incident) nor whether local persistence held. This one
  // line answers both at a glance — and lets a Windows-bench run verify a build
  // without UI automation. Desktop-only ; never rendered on web.
  let desktopDiag: HTMLParagraphElement | null = null;
  const updateDesktopDiag = (projects: ProjectRow[]): void => {
    if (!desktopDiag) return;
    const ver = desktopDiag.dataset.version ?? '…';
    const withDir = projects.filter((p) => p.local_dir).length;
    const withAns = projects.filter(
      (p) => p.discovery_answers && Object.keys(p.discovery_answers).length > 0,
    ).length;
    // The Anthropic account powering the agent (BYOAI) is a SEPARATE identity from
    // the Genesis login — surfacing it here answers "sur quel compte Claude l'agent
    // tourne ?" at a glance (the agent reuses that account's ambient memory).
    const claude = desktopDiag.dataset.claude ?? '…';
    const textEl = desktopDiag.querySelector<HTMLElement>('.welcome-desktop-diag__text');
    if (!textEl) return;
    textEl.textContent =
      `Genesis v${ver} · ${projects.length} projet(s) · ${withDir} avec dossier · ${withAns} avec réponses · Claude : ${claude}`;
  };
  if (detectPlatform() === 'desktop') {
    desktopDiag = document.createElement('p');
    desktopDiag.className = 'welcome-desktop-diag';
    desktopDiag.setAttribute('data-component', 'desktop-diag');
    const diagText = document.createElement('span');
    diagText.className = 'welcome-desktop-diag__text';
    diagText.textContent = 'Genesis v… · diagnostic';
    // Host for « l'encre du forfait » gauge — populated by refreshGauge below.
    const gaugeHost = document.createElement('span');
    gaugeHost.className = 'welcome-desktop-diag__gauge-host';
    desktopDiag.append(diagText, gaugeHost);
    void getAppVersion()
      .then((v) => { desktopDiag!.dataset.version = v; updateDesktopDiag(projectsCache_ext); })
      .catch(() => {});
    void claudeAuthStatus()
      .then((st) => {
        desktopDiag!.dataset.claude = st.linked ? (st.email ?? 'connecté') : 'non connecté';
        updateDesktopDiag(projectsCache_ext);
      })
      .catch(() => {});
    // Plan-usage gauge : reads the honest WindowState and self-hides when there's
    // no signal. Refreshed on mount + every 60 s ; the single shared timer is
    // cleared first so navigation re-mounts never stack intervals.
    const refreshGauge = async (): Promise<void> => {
      try {
        // §5 : before consent, the gauge stays dark and offers a one-time opt-in.
        if (!usageConsentGranted()) {
          gaugeHost.replaceChildren(renderConsentLine({
            locale: opts.locale,
            onGrant: () => { grantUsageConsent(); void refreshGauge(); },
          }));
          return;
        }
        const state = await getUsageBridge().getCurrentState();
        if (gaugeHasSignal(state)) {
          gaugeHost.replaceChildren(renderInkGauge(state, { locale: opts.locale }));
        } else {
          // Consenti mais pas encore de signal (aucun build lancé) : on NE masque
          // plus la jauge (disparition déroutante juste après « Activer ») — on
          // montre le placeholder « en attente de ton premier build ».
          gaugeHost.replaceChildren(renderInkGaugeAwaiting({ locale: opts.locale }));
        }
      } catch {
        gaugeHost.replaceChildren(); // graceful : never blocks the diag line
      }
    };
    void refreshGauge();
    if (usageGaugeTimer) clearInterval(usageGaugeTimer);
    usageGaugeTimer = setInterval(() => { void refreshGauge(); }, 60_000);
  }

  renderProjectsRegion(opts.locale, email, configured, composerHost, composerHost, {
    onDataChange: ({ projects }) => {
      projectsCache_ext = projects;
      renderProjectsList();
      updateDesktopDiag(projects);
    },
    exposeOpenRow: (fn) => { (canvasRight as unknown as { __openRow: typeof fn }).__openRow = fn; },
    exposeMountConverseChat: (fn) => { (canvasRight as unknown as { __mountConverseChat: typeof fn }).__mountConverseChat = fn; },
    setRenderIdle: (fn) => {
      renderIdleRef = () => {
        fn();
        showHero();
      };
    },
    // v7.8 : the project card (renderProjectPreviewCard collapsible) lands
    // in the LEFT col activeLeftFiche slot. The card's sub-blocks (chat,
    // attachments, discovery) route to the RIGHT col activeSlot via the
    // host options below — ONE of each, since the hosts get cleared by
    // openProjectInCanvasRef / onActivate before mount.
    resultHost: activeLeftFiche,
    chatBandHost: chatBand,
    chatHost: activeSlot,
    attachmentsHost: activeSlot,
    discoveryHost: activeSlot,
    deliveryHost: deliveryStrip,
    coverHost: coverStrip,
    onCoverMounted: () => placeBackButton(),
    onActivate: () => {
      showInterpretation();
      // Clear both slots before the region mounts its result, so chat /
      // attachments / discovery don't accumulate across renders.
      activeLeftFiche.innerHTML = '';
      activeSlot.innerHTML = '';
      queueMicrotask(() => placeBackButton());
    },
  });
  renderProjectsList();

  // Desktop diagnostic (version + store summary) sits just above the footer.
  if (desktopDiag) page.appendChild(desktopDiag);

  page.appendChild(renderFooter({ onSignOut: opts.onSignOut }));
  return frag;
}

// 2.7.1 item A — single source of truth for a picker tile. Exported so the
// dev responsive harness (src/dev/picker-top-preview.ts) renders the EXACT
// production tile (cf feedback_responsive_proof_must_render_the_production_mode).
// Click wiring stays at the call site.
export function buildProjectPickerTile(row: ProjectRow, locale: Locale): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'welcome-canvas-left__project-item';
  btn.setAttribute('data-component', 'canvas-left-project-item');
  btn.dataset.projectId = row.id;
  const title = document.createElement('span');
  title.className = 'welcome-canvas-left__project-item__title';
  title.textContent = row.title;
  const meta = document.createElement('span');
  meta.className = 'welcome-canvas-left__project-item__meta';
  const when = new Date(row.updated_at ?? row.created_at);
  const day = Number.isNaN(when.getTime())
    ? ''
    : ` · ${when.toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short' })}`;
  meta.textContent = `v${row.current_version_number ?? 1}${day}`;
  btn.append(title, meta);
  return btn;
}

interface ProjectsRegionOptions {
  // 2026-05-28 app-shell : sidebar owns the projects list + counter + nudges ;
  // the canvas owns the work loop. The region calls back when projects or
  // entitlement change so the parent can re-render the sidebar.
  onDataChange?: (state: { projects: ProjectRow[]; entitlement: Entitlement }) => void;
  // The parent exposes a "open a project in the canvas" hook so the
  // sidebar's onSelectProject callback can route through the region's own
  // renderEnrichedCardForRow (which holds the version/chat caches).
  exposeOpenRow?: (fn: (row: ProjectRow) => HTMLElement) => void;
  // Open-project chat (2026-06-22): mount a converse-bound refine chat in the
  // band for an already-started project (no discovery phase, no Magnifier).
  exposeMountConverseChat?: (fn: (row: ProjectRow) => void) => void;
  // Same idea for the "+ Nouvelle idée" button : it asks the region to
  // clear the canvas back to the composer-idle state.
  setRenderIdle?: (fn: () => void) => void;
  // 2026-05-28 v6 (2-col canvas) : when provided, the composer's onSuccess
  // result (magnify fiche card OR bootstrap next-steps card) mounts in
  // `resultHost` instead of below the composer in `region`. The composer
  // stays visible above (LEFT canvas) ; the result appears in the RIGHT
  // activeSlot. Without resultHost, the legacy single-column behavior
  // applies (result below composer).
  resultHost?: HTMLElement;
  // 2026-05-29 v7 : when provided, the project card's delivery blocks
  // (renderOnlinePreview / renderTerminalHandoff / desktop deliver button)
  // mount in `deliveryHost` (= the full-width delivery strip below both
  // cols) instead of inline at the bottom of the card body. Operator brief :
  // "livraison peut occuper la largeur de la page en bas des 2 colonnes".
  deliveryHost?: HTMLElement;
  // Chemise d'atelier : full-width cover band host (above the two cols). The
  // card builds the cover under its root ; renderEnrichedCardForRow moves it
  // here post-render. Cleared by the parent on hero/interpretation switches.
  coverHost?: HTMLElement;
  // Fired after each cover mount (incl. async self-re-renders) so the parent
  // can (re)place the « ← Nouvelle idée » button into the cover's back slot.
  onCoverMounted?: () => void;
  // 2026-05-29 v7.1 : fired BEFORE the composer's onSuccess mounts its result
  // into `resultHost`. The parent uses this to switch the shell layout from
  // single-col-hero to 2-col-active. v7.2 : also forwards the MagnifyResult
  // so the parent can populate the LEFT col fiche immediately (without
  // waiting for the saveProject roundtrip).
  onActivate?: (init?: { result?: MagnifyResult; idea?: string }) => void;
  // v7.6 : when chatHost is provided, the chat wrap mounts in chatHost
  // (= RIGHT col activeSlot) instead of being prepended to the card.
  // Clear-before-mount avoids the 2-bar bug from v7.6.
  chatHost?: HTMLElement;
  // v7.8 : when set, project attachments route to attachmentsHost (RIGHT
  // col activeSlot) via renderProjectPreviewCard's railTarget.
  attachmentsHost?: HTMLElement;
  // v7.8 : when set, the discovery panel mounts standalone in discoveryHost
  // instead of inline in the card.
  discoveryHost?: HTMLElement;
  // 2026-06-21 chat-first : the discovery chat mounts in this full-width band at
  // the TOP of the shell (a direct shell child → immune to the composer-host
  // hide rule + the canvasLeft fiche-swap), so the conversation stays pinned at
  // the top across the hero → active transition.
  chatBandHost?: HTMLElement;
}

function renderProjectsRegion(
  locale: Locale,
  email: string,
  configured: boolean,
  rail: HTMLElement,
  main: HTMLElement,
  regionOpts: ProjectsRegionOptions = {},
): void {
  // 2026-05-28 — no more rail/main split, no more rail-zone swap. The region
  // is one canvas-bound section ; the freemium counter + projects list moved
  // to the welcome-sidebar (rebuilt by the parent on data change via
  // onDataChange). `rail` and `main` are kept as parameters to avoid a
  // signature break ; both should be the same node (the canvas). `email`
  // was only used by the removed renderMyProjectsPanel ; kept in signature
  // for callers' readability.
  void rail; void email; void configured;
  const region = document.createElement('section');
  region.className = 'welcome-projects-region';
  region.setAttribute('data-component', 'welcome-projects-region');
  main.appendChild(region);

  // No-op kept for back-compat with renderEnrichedCardForRow's calls below.
  // The original "swap to dashboard rail" semantics are subsumed by the
  // sidebar being always-present.
  const showDashboardRail = (): void => { /* no-op in app-shell */ };

  // Local cache of the user's saved projects. Populated async on first mount,
  // refreshed after every successful save so the "Mes projets" panel reflects
  // the new entry without a full re-mount of /welcome. The render functions
  // close over this and re-read it on each pass.
  let projectsCache: ProjectRow[] = [];
  // Guided first-run capture (beat ④ gating). isGuidedFirstRun flips to false the
  // instant the first idea is magnified (ideasConsumed > 0), which would end the
  // guided treatment exactly when the post-magnify folded discovery should appear.
  // So once the guided deposit-box entry decides this is a guided run, we LATCH it
  // for the rest of the run and read the latch (not a live recompute) when building
  // the post-magnify card. Graduation still applies on the next visit (a fresh page
  // load recomputes isGuidedFirstRun). « passer le guidage » clears it via dismissal.
  let guidedRun = false;
  // True once the real projects list has loaded — the guided latch must NOT fire on
  // the empty-cache synchronous first paint (every mount starts with 0 projects),
  // only once we KNOW the user is a genuine blank slate.
  let projectsLoaded = false;
  // Operator/dogfood hatch: ?force-guided forces the guided first-run treatment ON
  // for ANY account (a graduated operator would otherwise never see it, since the
  // run gates on a blank slate). Presentation-only (no data mutation, no auth/quota
  // change) → safe to leave in prod; harmless if the URL is shared.
  let forceGuided = false;
  try { forceGuided = new URLSearchParams(window.location.search).has('force-guided'); } catch { /* no URL ctx */ }
  // The user's idea string is kept alongside the in-flight result so the save
  // callback can persist it ; MagnifyResult doesn't carry the original idea.
  let currentIdea = '';
  // 2026-06-21 chat-first : a conversation (discovery or, post-magnify, converse)
  // is in progress. Gates the AUTOMATIC renderIdle refreshes (entitlement / sync
  // loads) so a live chat + its closure state (convRow…) is never silently wiped.
  // Reset to false each time renderIdle builds a fresh entry chat.
  let chatStarted = false;
  const isIdleEntry = (): boolean =>
    !!regionOpts.chatBandHost && !regionOpts.chatBandHost.hidden && !chatStarted;
  // Monotonic "active view" token, bumped each time the canvas switches (a fresh
  // discovery entry via renderIdle, or opening a project via mountConverseChat).
  // An async converse turn captures it and only paints the fiche if its project
  // is still the active view — prevents a stale in-flight turn from clobbering a
  // project the user has since navigated to.
  let openGen = 0;
  // CP-1 Task 6: real entitlement. Loaded async once alongside listProjects.
  // Defaults to the safe unauthenticated state (remaining:1) until the fetch
  // resolves so the composer starts active. Re-rendered via renderIdle() once
  // it resolves so the composer and counter reflect the real values.
  let entitlementCache: Entitlement = {
    freeQuota: 1, purchasedCredits: 0, ideasConsumed: 0,
    remaining: 1, desktopLicense: false, cloudStatus: 'none', cloudUntil: null,
  };

  // Carte unifiée Task 5 — desktop-only sync state. Read once on init, updated
  // after a successful cloud pull. Web → always {} / null (guards below gate).
  // renderEnrichedCardForRow reads these synchronously (no await per-row).
  let syncMapCache: Record<string, string> = {};
  let lastSyncedAtCache: string | null = null;
  // Load desktop sync state once. Safe no-op on web (detectPlatform guard). The
  // first card render may run before this resolves (fire-and-forget), so when sync
  // state actually exists we repaint via renderIdle() so badges flip untied→synced
  // instead of being stuck on the empty-cache snapshot. (renderIdle is defined later
  // in this scope; the .then() runs async, after it's assigned.)
  if (detectPlatform() === 'desktop') {
    void Promise.all([loadSyncMap(), getLastSyncedAt()]).then(([sm, ls]) => {
      syncMapCache = sm;
      lastSyncedAtCache = ls;
      if (Object.keys(sm).length > 0 && isIdleEntry()) renderIdle();
    }).catch((err) => { console.warn('[welcome] sync-meta load failed (non-fatal):', err); });
  }

  // Platform AI bridge (desktop-app step 2). On the web this resolves to the
  // edge-fn client (`webAiBridge.magnify` → `magnifyIdea`) so behaviour is
  // byte-for-byte unchanged ; in the Tauri desktop shell it routes magnify to
  // the user's local `claude` CLI (their subscription). Resolved once — the
  // platform never changes at runtime. The UI below never branches on platform.
  const ai = getAiBridge();

  // Platform data bridge (desktop-app step 4). Web → the Supabase project API
  // (byte-for-byte unchanged) ; desktop → local JSON stores. Resolved once. The
  // version/discovery/backend/chat-history calls below all route through this so
  // the desktop dashboard never reaches for Supabase.
  const data = getDataBridge();

  // SPA nav to /tarifs — pushState + popstate lets the existing router's
  // popstate listener mount the route without a full reload (no router import).
  const navigateTarifs = (): void => {
    history.pushState(null, '', '/tarifs');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // CP-1 Task 6: show quota-exhausted message in the composer area and surface a
  // /tarifs CTA. Called from both the bootstrap-early throw path and the magnify
  // 402 path. Re-renders the composer in exhausted mode (remaining=0).
  const showQuotaExhausted = (): void => {
    entitlementCache = { ...entitlementCache, remaining: 0 };
    clearRegion();
    renderIdle();
  };

  // 2026-05-28 app-shell : the freemium counter + upsell + cloud-sync button
  // moved to the welcome-sidebar (which the parent re-renders via the
  // onDataChange callback). The region notifies the parent whenever the
  // projectsCache or entitlementCache changes so the sidebar stays in sync.
  const notifyDataChange = (): void => {
    regionOpts.onDataChange?.({
      projects: projectsCache,
      entitlement: entitlementCache,
    });
  };

  // P3 — desktop Atelier pulls web-created projects. On desktop, the project list
  // reads the local JSON store, so ideas created on the web never appear until we
  // pull them down. Fire a non-blocking cloud-sync on load (no-op on web / when not
  // signed in); when it pulls anything, re-list from the (now-updated) local store
  // and refresh the canvas + sidebar. The page has already painted local projects,
  // so this only ever ADDS the missing cloud ones — never blocks first paint.
  const pullDesktopProjectsThenRefresh = (): void => {
    void syncDesktopProjectsOnLoad()
      .then((res) => {
        if (!res || (res.projects === 0 && res.versions === 0 && res.versionsPushed === 0 && res.attachments === 0)) return undefined;
        return getDataBridge().listProjects().then((rows) => {
          projectsCache = rows;
          if (isIdleEntry()) renderIdle();
          notifyDataChange();
        });
      })
      .catch((err) => console.warn('[welcome] desktop cloud-sync pull failed (non-fatal):', err));
  };

  // 2026-05-28 app-shell : renderMyProjectsPanel removed (was rendered inline
  // in the canvas, now lives in the welcome-sidebar). renderMyProjects import
  // is also dropped at the top of the file. The sidebar's onSelectProject
  // routes through openProjectInCanvasRef → renderProjectPreviewCardForRow.

  // Build a card that supports two regen modes :
  // - "↻ Régénérer" : re-call Magnify with the same idea, no context →
  //   produces a fresh variation of the same prompt. Operator can hit it
  //   multiple times until they like one.
  // - "↻ Régénérer enrichi" : same idea + attachments context → produces
  //   a manifest tailored to the uploaded files.
  // Both replace the card in place ; a separate "Nouvelle idée" path
  // lives in the composer up top (which appears on full /welcome reload).
  // Per-project version cache. Lazy-loaded on first card render for a
  // given project ; refreshed after every regen so the chip "vN / M"
  // stays in sync without a re-fetch in the click path.
  const versionsCache = new Map<string, ProjectVersionRow[]>();

  // 2026-06-02 — the per-project chat (converse) was removed: it was friction that
  // overlapped with magnify + discovery + the regen buttons, and was never part of
  // the original flow. Definition = magnify + discovery questions; refinement =
  // régénérer / relancer la découverte. On desktop the post-definition step is the
  // AGENT building the project (mountAgentPanel), not a conversation.

  // Renders a card, then triggers an async version-list fetch in the
  // background and re-renders with the chip once it lands. Avoids
  // blocking the initial paint on a Postgres round-trip when the user
  // re-opens a project from Mes Projets.
  //
  // `regenJustHappened` is set by callers that triggered the regen
  // themselves (onRegenerate / onRegenerateEnriched / onFinishDiscovery)
  // so the success banner appears on the new card. Set to undefined on
  // initial mount or version-switch (no banner needed there).
  const renderEnrichedCardForRow = (
    row: ProjectRow,
    result = projectRowToMagnifyResult(row),
    regenJustHappened?: VersionContextKind,
    expanded = false,
  ): HTMLElement => {
    let card: HTMLElement;
    const cachedVersions = versionsCache.get(row.id) ?? [];
    // v7.8.8 fix : this function mounts side-effect blocks into shared hosts
    // (RIGHT col activeSlot : attachments / backend / discovery / chat ; and the
    // FOOTER deliveryStrip : online preview / terminal handoff / deliver) that
    // are NOT children of the card root. The async self-re-renders
    // (loadConversationHistory.then / refreshVersionsCache.then) do
    // card.replaceWith(next) on the LEFT col, which leaves every shared-host
    // block behind → the new render appends DUPLICATES (operator 2026-05-29 :
    // "tout en double dans la colonne de droite et dans le footer"). Clear every
    // block we (re)mount, in every host, BEFORE building the new card.
    // Note the prior fix missed `.discovery-panel` (the active panel form, vs the
    // CTA slot) AND the delivery strip body entirely.
    const clearStale = (host: HTMLElement | undefined | null, selectors: string): void => {
      if (!host) return;
      for (const stale of host.querySelectorAll(selectors)) stale.remove();
    };
    // 2.7.1 item B : the fold wrapper ([data-component="ppc-section-fold"])
    // hosts attachments/discovery on delivered-desktop opens — clear it too,
    // else re-renders leave empty wrappers behind (inner nodes match alone).
    clearStale(regionOpts.attachmentsHost, '.project-attachments, .project-preview-card__backend, .project-preview-card__discovery-slot, .discovery-panel, .folded-discovery, [data-component="ppc-section-fold"]');
    clearStale(regionOpts.discoveryHost, '.project-preview-card__discovery-slot, .discovery-panel, .folded-discovery, [data-component="ppc-section-fold"]');
    clearStale(regionOpts.chatHost, '[data-component="project-chat-top"]');
    // Delivery strip : keep the one-time heading, clear the mounted blocks in
    // its body so they don't stack on every re-render (the footer duplication).
    clearStale(regionOpts.deliveryHost, '.welcome-delivery-strip__body > *');
    // A fresh / just-regenerated fiche opens expanded (you want to see what
    // changed) ; a re-open from "Tes projets" opens collapsed (the pinned
    // summary). Threaded into the card's `expanded` + the self-re-renders that
    // aren't themselves a regen (restart-discovery, version-chip async mount).
    const isExpanded = expanded || !!regenJustHappened;
    // 2026-05-28 app-shell : the fiche rail is gone — every block (📎 files,
    // 🌐 online preview, ⚙ backend) stacks inline in the card itself. The
    // upsell moved to the welcome-sidebar so it's no longer mounted in the
    // card either. renderProjectPreviewCard's railTarget/backendTarget are
    // passed undefined ; the component falls back to card-internal mounting.
    // Desktop discover context, read at CALL time (the in-memory file context
    // grows as the user drops files). Web ignores it — the discover edge fn
    // reads the project + its Storage attachments server-side — so passing it is
    // byte-neutral on web and gives the desktop bridge its text inputs.
    const discoverCtx = () => {
      const { entries } = getProjectContext(row.user_id, row.id);
      return {
        idea: row.idea,
        locale,
        textContext: buildContextString(row.user_id, row.id),
        seen: entries.map((e) => e.filename),
      };
    };
    // Carte unifiée Task 5 — compute sync badge from cached state (no await per-row).
    const _platform = detectPlatform();
    const _headerBadge = renderSyncBadge({
      platform: _platform,
      status: syncStatusFor(row, _platform, syncMapCache),
      lastSyncedAt: lastSyncedAtCache,
    });
    // ── Provision / deploy handlers, shared between the card backend slot and
    // the delivery-view provision CTA (2.7.1 item #2). Extracted as named locals
    // so both surfaces route through the EXACT same row/cache mutations.
    const provisionBackendForRow = async ({ handle, model }: { handle?: string; model: BaseModel }) => {
      // 271 slug fix: default the backend handle to the slugified project title
      // (else the edge fn falls back to an ugly proj-<id8> subdomain).
      const res = await data.provisionBackend(row.id, { handle: handle ?? slugify(row.title), model });
      row.backend_kind = model;
      row.backend_url = res.backendUrl;
      row.backend_anon_key = res.backendAnonKey;
      const cached = projectsCache.find((p) => p.id === row.id);
      if (cached) {
        cached.backend_kind = model;
        cached.backend_url = res.backendUrl;
        cached.backend_anon_key = res.backendAnonKey;
      }
      if (detectPlatform() === 'desktop' && row.local_dir) {
        await writeProjectEnv(row.local_dir, res.backendUrl, res.backendAnonKey);
      }
      return res;
    };
    // When FULL_SUPABASE_REQUIRES_CLOUD is on (post-launch), the wizard routes a
    // full-supabase choice here instead of provisioning — send the user to the
    // Cloud upsell. Wired now (not after the flag flip) so enabling the gate can
    // never silently no-op the confirm. Off at launch → this is never called.
    const cloudUpsellForRow = () => {
      history.pushState(null, '', '/tarifs?mode=cloud');
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    // deploy_frontend (gate on): "Mettre en ligne mon appli" → deploy the site
    // (+ optional base when model !== 'none'), return the outcome so the wizard
    // shows build-progress → live URL. Desktop pushes to Forgejo first (the code
    // is local; the deployer builds FROM the repo). Off at launch → never called.
    const deployFrontendForRow = async ({ handle, model, redeploy }: { handle: string; model: BackendModel; redeploy?: boolean }): Promise<DeployOutcome> => {
      // Desktop: ensure the code is on Forgejo before the deployer can build it.
      if (detectPlatform() === 'desktop' && !row.forgejo_repo_url) {
        const r = await bootstrapForgejoRepo(row.id);
        row.forgejo_repo_url = r.html_url;
        const c = projectsCache.find((p) => p.id === row.id);
        if (c) c.forgejo_repo_url = r.html_url;
      }
      // Optional base, only when the user asked for one.
      if (model !== 'none') {
        await provisionBackendForRow({ handle, model });
      }
      // Deploy the front.
      const dep = await data.deployFrontend(row.id, { handle, redeploy });
      row.deploy_url = dep.deployUrl;
      row.deploy_status = dep.status;
      row.deploy_id = dep.deployId;
      const cd = projectsCache.find((p) => p.id === row.id);
      if (cd) { cd.deploy_url = dep.deployUrl; cd.deploy_status = dep.status; cd.deploy_id = dep.deployId; }
      return { deployUrl: dep.deployUrl, status: dep.status };
    };
    const pollDeployStatusForRow = async () => {
      const s = await data.getDeployStatus(row.id);
      row.deploy_status = s.status;
      if (s.deployUrl) row.deploy_url = s.deployUrl;
      const cd = projectsCache.find((p) => p.id === row.id);
      if (cd) { cd.deploy_status = s.status; if (s.deployUrl) cd.deploy_url = s.deployUrl; }
      // The wizard's poll loop only ever calls this while a just-triggered deploy is
      // actively building — torn_down (null) can't happen in that window in practice.
      // Narrow to the wizard's DeployStatus contract (building/live/failed) rather than
      // widen paintProgress for a state it can't render; the row/cache above already
      // got the real (possibly null) status for the card's own idle-state rendering.
      return { ...s, status: s.status ?? 'failed' as const };
    };
    // 2.7.1 item #2 — the delivery view (agent panel history col) re-offers the
    // provision CTA when the project has no backend yet. Built per-call so
    // hasBackend reads row.backend_kind at MOUNT time (e.g. after the user
    // provisioned through the card slot, a later delivery mount shows no CTA).
    const deliveryProvisionForRow = (): DeliveryProvision => ({
      hasBackend: !!row.backend_kind,
      title: row.title,
      onProvisionBackend: provisionBackendForRow,
      onCloudUpsell: cloudUpsellForRow,
      onPollStatus: pollDeployStatusForRow,
      onDeployFrontend: deployFrontendForRow,
    });
    // Code-continuity (clone-first delivery): guarantee the project has a LIVE
    // Forgejo repo + read token before we clone it. The repo is the durable
    // store that lets the built code follow the user across workstations. If the
    // row already has both, reuse them ; otherwise bootstrap (idempotent — the
    // edge fn persists forgejo_repo_url + forgejo_clone_token server-side and
    // returns them). Returns null on failure → caller degrades to a local
    // scaffold (the build still works, it just isn't cloud-backed yet).
    // The canonical git HTTP URL (.git) for a project's repo — what clone/push
    // authenticate against (Task 0 verified the write token against the .git URL).
    // Falls back to the raw html_url if it can't be derived.
    const repoCloneUrl = (htmlUrl: string): string => deriveForgejoUrls(htmlUrl).clone_url || htmlUrl;
    const ensureProjectRepo = async (): Promise<{ cloneUrl: string; cloneToken: string | null } | null> => {
      if (row.forgejo_repo_url && row.forgejo_clone_token) {
        return { cloneUrl: repoCloneUrl(row.forgejo_repo_url), cloneToken: row.forgejo_clone_token };
      }
      try {
        // A project pulled from the cloud gets a NEW LOCAL id (cloud-sync assigns
        // one; the syncmap bridges localId→cloudId). forgejo-bootstrap looks the
        // project up by its CLOUD id, so sending the local id → 404 project_not_found
        // (the « Ce projet n'est pas relié au cloud » smoke failure on the GMK).
        // Resolve the cloud id via the syncmap; fall back to row.id for same-id
        // (web-opened / not-yet-synced) projects.
        const cloudProjectId = (await loadSyncMap())[row.id] ?? row.id;
        const r = await bootstrapForgejoRepo(cloudProjectId);
        row.forgejo_repo_url = r.html_url;
        row.forgejo_clone_token = r.clone_token ?? row.forgejo_clone_token ?? null;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) { cached.forgejo_repo_url = r.html_url; cached.forgejo_clone_token = row.forgejo_clone_token; }
        return { cloneUrl: repoCloneUrl(r.html_url), cloneToken: row.forgejo_clone_token };
      } catch (e) {
        console.warn('[delivery] ensureProjectRepo failed', e);
        return null;
      }
    };
    // cloudSave target for mountAgentPanel — uses the canonical .git URL for pushes.
    const cloudSaveTarget = (): { projectId: string; repoUrl: string } | undefined =>
      row.forgejo_repo_url ? { projectId: row.id, repoUrl: repoCloneUrl(row.forgejo_repo_url) } : undefined;
    card = renderProjectPreviewCard({
      result,
      idea: row.idea,
      // Carte unifiée Task 5 — badge computed above. collapsible:true below, so the
      // card mounts it into the surviving .project-fiche-artefact__head (not __head).
      headerBadge: _headerBadge,
      // v7.8 : attachments route to attachmentsHost (RIGHT col activeSlot)
      // when set ; backend stays inline with the card.
      railTarget: regionOpts.attachmentsHost,
      backendTarget: undefined,
      // v7.8 : always collapsible (the ✦ ta fiche pinned summary with
      // click-to-expand from SP-2). Forced regardless of isSetupComplete.
      collapsible: true,
      expanded: isExpanded,
      // Desktop delivered projects auto-collapse the fiche to give room to
      // the agent/build panel. Re-openable via the existing toggle header.
      collapsed: detectPlatform() === 'desktop' && !!row.local_dir,
      // Chemise d'atelier — the cover's fil d'état, computed from the row.
      // onlineUrl: real deploy host when live, else the projected shortHandle
      // (same generator as the wizard prefill — no more « nom à rallonge »).
      cover: {
        createdAt: row.created_at,
        locale,
        built: !!(row.local_dir || row.forgejo_repo_url),
        dbActive: !!row.backend_kind,
        online: row.deploy_status ?? null,
        onlineUrl: row.deploy_url
          ? row.deploy_url.replace(/^https?:\/\//, '')
          : `${shortHandle(row.title)}.genesis-platform.fr`,
      },
      ledgerHints: {
        planCount: result.first_steps?.length || undefined,
        discoveryCount: row.discovery_answers
          ? Object.keys(row.discovery_answers).length || undefined
          : undefined,
      },
      onRegenerate: async () => {
        const fresh = await ai.magnify(row.idea, locale);
        const newVersion = await data.saveProjectVersion(row.id, fresh, 'regen');
        row.current_version_number = newVersion;
        await refreshVersionsCache(row.id);
        // Mutate the row's current fields so a re-open from Mes Projets
        // reflects the latest content without re-fetch.
        applyResultToRow(row, fresh);
        const next = renderEnrichedCardForRow(row, fresh, 'regen');
        card.replaceWith(next);
        scrollCardIntoView(next);
      },
      userId: row.user_id,
      projectId: row.id,
      onRegenerateEnriched: async () => {
        const ctx = buildContextString(row.user_id, row.id);
        const fresh = await ai.magnify(row.idea, locale, ctx);
        const newVersion = await data.saveProjectVersion(row.id, fresh, 'enriched', ctx);
        row.current_version_number = newVersion;
        await refreshVersionsCache(row.id);
        applyResultToRow(row, fresh);
        const next = renderEnrichedCardForRow(row, fresh, 'enriched');
        card.replaceWith(next);
        scrollCardIntoView(next);
      },
      forgejoRepoUrl: row.forgejo_repo_url,
      forgejoCloneToken: row.forgejo_clone_token,
      onPushForgejo: async () => {
        const r = await bootstrapForgejoRepo(row.id);
        row.forgejo_repo_url = r.html_url;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) cached.forgejo_repo_url = r.html_url;
        // Chemise mode hides the next-steps panel inside the cover toolbar
        // (the clone guidance lives in the delivery strip) — but the strip
        // only renders the terminal handoff when forgejo_repo_url was set at
        // RENDER time. Re-render after the slot settles so a FIRST push
        // immediately surfaces the handoff (else: dead-end until re-open).
        queueMicrotask(() => {
          if (!card.isConnected) return;
          const next = renderEnrichedCardForRow(row, result, undefined, true);
          card.replaceWith(next);
        });
        return { html_url: r.html_url };
      },
      // 2026-05-19 Phase 3.A BYO — backend config. Saved values mutate the
      // local row immediately (no refetch needed) + projectsCache stays in
      // sync. forgejo-bootstrap on next push reads the freshly-saved row
      // and bakes the values into the cloned repo's `.env.local`.
      backendKind: row.backend_kind,
      backendUrl: row.backend_url,
      backendAnonKey: row.backend_anon_key,
      onSaveBackend: async (url, anonKey) => {
        await data.saveProjectBackend(row.id, url, anonKey);
        row.backend_kind = 'byo-supabase';
        row.backend_url = url;
        row.backend_anon_key = anonKey;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) {
          cached.backend_kind = 'byo-supabase';
          cached.backend_url = url;
          cached.backend_anon_key = anonKey;
        }
      },
      onClearBackend: async () => {
        await data.clearProjectBackend(row.id);
        row.backend_kind = null;
        row.backend_url = null;
        row.backend_anon_key = null;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) {
          cached.backend_kind = null;
          cached.backend_url = null;
          cached.backend_anon_key = null;
        }
      },
      backendHostSignedOut: !email,
      // Provision/deploy handlers extracted above (shared with the delivery CTA).
      onProvisionBackend: provisionBackendForRow,
      onCloudUpsell: cloudUpsellForRow,
      onDeployFrontend: deployFrontendForRow,
      onPollDeployStatus: pollDeployStatusForRow,
      onStartDiscovery: async () => {
        const fresh = await ai.discover(row.id, discoverCtx());
        // Persist the snapshot so the Q&A panel restores on reload. On web this is a
        // no-op (the discover edge fn already wrote summary/questions server-side); on
        // DESKTOP it's the fix for Bug B — the local discover() bridge only RETURNS the
        // data, so without this the questions/summary never reach projects.json and the
        // saved answers were orphaned on reopen. Also mirror onto the in-memory row +
        // cache so an in-session rerender sees the stored discovery (hasStoredDiscovery).
        await data.saveDiscovery(row.id, fresh);
        row.discovery_summary = fresh.discovery_summary;
        row.discovery_questions = fresh.questions;
        row.discovery_attachments_seen = fresh.attachments_seen;
        row.discovery_attachments_skipped = fresh.attachments_skipped;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) {
          cached.discovery_summary = fresh.discovery_summary;
          cached.discovery_questions = fresh.questions;
          cached.discovery_attachments_seen = fresh.attachments_seen;
          cached.discovery_attachments_skipped = fresh.attachments_skipped;
        }
        return fresh;
      },
      // 2026-05-22 — draft autosave, decoupled from the re-magnify below. The
      // discovery-panel fires this on every textarea blur + on the explicit
      // "Enregistrer mes réponses" button, so typed answers survive a reload/
      // navigation (the prior design only persisted them as a side-effect of
      // onFinishDiscovery — which ALSO re-magnifies, so keyless users 403'd and
      // anyone who left before submitting lost their input). Restored via
      // storedAnswers on reopen. Throws on failure so the panel's save button
      // can flag it; the blur path swallows (silent autosave).
      onSaveDraft: async (answers) => {
        await data.saveDiscoveryAnswers(row.id, answers);
        row.discovery_answers = answers;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) cached.discovery_answers = answers;
      },
      onFinishDiscovery: async (answers, discovery) => {
        // v26 — persist the user's answers on the project row before
        // (or in parallel with) the re-magnify call. If a refresh
        // happens mid-flight, the answers survive and the user can
        // resume from where they left off.
        try {
          await data.saveDiscoveryAnswers(row.id, answers);
          row.discovery_answers = answers;
          const cached = projectsCache.find((p) => p.id === row.id);
          if (cached) cached.discovery_answers = answers;
        } catch (err) {
          console.warn('[welcome] saveDiscoveryAnswers failed (non-fatal):', err);
        }
        const baseCtx = buildContextString(row.user_id, row.id);
        const refined = buildRefinementContext(baseCtx, discovery, answers);
        const fresh = await ai.magnify(row.idea, locale, refined);
        const newVersion = await data.saveProjectVersion(row.id, fresh, 'discovery', refined);
        row.current_version_number = newVersion;
        await refreshVersionsCache(row.id);
        applyResultToRow(row, fresh);
        const next = renderEnrichedCardForRow(row, fresh, 'discovery');
        card.replaceWith(next);
        scrollCardIntoView(next);
      },
      // v26 — restore path : if the row already has stored discovery
      // (from a prior run), the card renders the discovery-panel
      // directly with prior answers pre-filled. Otherwise the standard
      // post-upload CTA flow.
      storedDiscovery: hasStoredDiscovery(row) ? {
        discovery_summary: row.discovery_summary!,
        questions: row.discovery_questions!,
        attachments_seen: row.discovery_attachments_seen ?? [],
        attachments_skipped: row.discovery_attachments_skipped ?? [],
      } : undefined,
      storedAnswers: row.discovery_answers ?? undefined,
      onRestartDiscovery: async () => {
        // Force a fresh Anthropic call (re-scans current attachments).
        // The edge fn updates discovery_summary/questions/run_at on
        // public.projects ; we then re-render the card with the new
        // result + clear prior answers (they don't match the new
        // questions).
        const fresh = await ai.discover(row.id, discoverCtx());
        // Persist the new snapshot (desktop: the local discover() only returns it; web:
        // edge fn already wrote it) + clear the now-mismatched answers on disk too, so a
        // reload after a restart restores the NEW questions and not stale answers (Bug B).
        await data.saveDiscovery(row.id, fresh);
        await data.saveDiscoveryAnswers(row.id, {});
        row.discovery_summary = fresh.discovery_summary;
        row.discovery_questions = fresh.questions;
        row.discovery_attachments_seen = fresh.attachments_seen;
        row.discovery_attachments_skipped = fresh.attachments_skipped;
        row.discovery_answers = null;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) {
          cached.discovery_summary = fresh.discovery_summary;
          cached.discovery_questions = fresh.questions;
          cached.discovery_answers = null;
        }
        const next = renderEnrichedCardForRow(row, result, undefined, isExpanded);
        card.replaceWith(next);
        scrollCardIntoView(next);
      },
      currentVersion: row.current_version_number,
      versions: cachedVersions,
      onSwitchVersion: async (versionNumber) => {
        await data.switchToProjectVersion(row.id, versionNumber);
        const targetV = cachedVersions.find((v) => v.version_number === versionNumber);
        if (!targetV) return;
        row.current_version_number = versionNumber;
        applyResultToRow(row, projectVersionToMagnifyResult(targetV));
        const next = renderEnrichedCardForRow(row, projectVersionToMagnifyResult(targetV), undefined, true);
        card.replaceWith(next);
        scrollCardIntoView(next);
      },
      regenJustHappened,
      // Guided first-run beat ④ — fold the deep discovery into the chat (latched
      // at the guided deposit-box entry; see guidedRun). Non-guided runs are unchanged.
      guided: guidedRun,
      // Beat ⑤ (§5.9): the « ✦ Construis-le » beacon leads to the build/deliver zone.
      onBeaconBuild: () => regionOpts.deliveryHost?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    });
    // v7.8 — operator Q3 : "RIGHT col = chat + attachments + discovery, ONCE
    // each". The card creates the discovery panel/CTA INSIDE its root (LEFT
    // col); move it out to discoveryHost (= activeSlot, RIGHT col) so the
    // user sees the discovery questions alongside files & chat. We keep the
    // node, just relocate.
    if (regionOpts.discoveryHost) {
      // 2.7.1 item B — when the card folded the discovery section (delivered
      // desktop, collapsed:true), move the fold WRAPPER so the folded state
      // travels with the node; otherwise the bare panel/slot as before.
      const discoveryNode =
        card.querySelector('[data-component="ppc-section-fold"][data-fold="discovery"]')
        ?? card.querySelector('.discovery-panel, .folded-discovery, .project-preview-card__discovery-slot');
      if (discoveryNode) regionOpts.discoveryHost.appendChild(discoveryNode);
    }
    // Chemise d'atelier — the cover travels to the full-width band above the
    // two cols. The host is cleared on hero/interpretation switches; clear
    // again here so the async self-re-render (refreshVersionsCache.then)
    // never stacks two covers.
    if (regionOpts.coverHost) {
      const coverNode = card.querySelector('[data-component="project-cover"]');
      if (coverNode) {
        regionOpts.coverHost.innerHTML = '';
        regionOpts.coverHost.appendChild(coverNode);
        // The back button lives in the cover's back slot — a self-re-render
        // replaces the cover (slot empty again), so let the parent re-place it.
        regionOpts.onCoverMounted?.();
      }
    }
    // 2026-05-29 v7 : delivery blocks (online preview / terminal handoff /
    // desktop deliver) mount in the FULL-WIDTH delivery strip below both
    // cols when `deliveryHost` is provided. Without it (legacy), they stack
    // inline at the bottom of the card. Operator brief : "livraison peut
    // occuper la largeur de la page en bas des 2 colonnes".
    const deliveryHost = regionOpts.deliveryHost ?? card;
    // When mounting into the strip, also include a small heading once.
    if (regionOpts.deliveryHost && !regionOpts.deliveryHost.querySelector('.welcome-delivery-strip__heading')) {
      const heading = document.createElement('h3');
      heading.className = 'welcome-delivery-strip__heading';
      heading.textContent = t('welcome.delivery.heading');
      regionOpts.deliveryHost.appendChild(heading);
      const body = document.createElement('div');
      body.className = 'welcome-delivery-strip__body';
      body.setAttribute('data-component', 'delivery-strip-body');
      regionOpts.deliveryHost.appendChild(body);
    }
    const deliveryBody = regionOpts.deliveryHost?.querySelector<HTMLElement>('.welcome-delivery-strip__body') ?? deliveryHost;

    // Cloud-resume state (desktop, project in the cloud but not on this machine yet):
    // the delivery strip LEADS with the prominent resume panel and HIDES the build
    // rubrics (« construire en local » + the not-delivered emplacement) so the user
    // never recreates a folder over the cloud one. Everything returns after reprise.
    const isCloudResume = detectPlatform() === 'desktop' && !!row.forgejo_repo_url && !row.local_dir;
    // …but a project already deployed LIVE keeps its « en ligne » section + emplacement
    // even in cloud-resume state so its live URL stays visible/reachable (the online
    // section is the deployed result, not a build/recreation rubric).
    const isLiveDeployed = row.deploy_status === 'live' && !!row.deploy_url;

    // Carte unifiée Task 7 — online-preview mounts on BOTH platforms regardless
    // of forgejo_repo_url (desktop sees the "bientôt" state, web sees it too).
    // Wrapped in a shared « en ligne » section header via renderProjectSection.
    // Skipped in cloud-resume state UNLESS the project is live (keep the URL visible).
    if (!isCloudResume || isLiveDeployed) {
      // Deploy-expiry B3 — the countdown/banner props read the CURRENT row on
      // every paint; « Redéployer » re-runs the existing deploy path on the
      // SAME subdomain (handle from deploy_url — the edge fn's title fallback
      // may differ when the user customized the handle in the wizard) with no
      // backend re-provision (model 'none'). Repaints replace the element in
      // place, gated on the active view (openGen) so a poll ending after the
      // user navigated away never paints over the new view.
      const onlineGen = openGen;
      let onlineEl: HTMLElement;
      const repaintOnline = (): void => {
        if (onlineGen !== openGen || !onlineEl.isConnected) return;
        const next = buildOnlineEl();
        onlineEl.replaceWith(next);
        onlineEl = next;
      };
      const onRedeploy = makeRedeployHandler(
        () => handleFromDeployUrl(row.deploy_url) ?? shortHandle(row.title),
        {
          isBuilding: () => row.deploy_status === 'building',
          // redeploy: true — the row is LIVE; without it the edge fn (and
          // gen-deployer) short-circuit idempotently and the TTL never resets.
          deploy: (handle) => deployFrontendForRow({ handle, model: 'none', redeploy: true }),
          poll: pollDeployStatusForRow,
          becameLive: () => row.deploy_status === 'live',
          // Optimistic +14 j mirror of the server's ttlDays:14 — Part C's
          // status-poll persist supersedes it with the ledger's real value.
          applyExpiry: (iso) => {
            row.deploy_expires_at = iso;
            const cd = projectsCache.find((p) => p.id === row.id);
            if (cd) cd.deploy_expires_at = iso;
          },
          repaint: repaintOnline,
        },
      );
      const buildOnlineEl = (): HTMLElement => renderOnlinePreview({
        title: row.title,
        createdAt: row.created_at,
        // deploy_frontend state (null until the user "met en ligne" — gate off → stays "bientôt").
        deployStatus: row.deploy_status,
        deployUrl: row.deploy_url,
        deployExpiresAt: row.deploy_expires_at,
        cloudEnabled: cloudEnabled(),
        onKeepOnline: cloudUpsellForRow,
        onRedeploy,
        onDownload: async () => {
          const { buildProjectZip, downloadBlob } = await import('../projects/zip');
          const { blob, filename } = await buildProjectZip(projectRowToMagnifyResult(row), row.idea);
          downloadBlob(blob, filename);
        },
        onProDomain: navigateTarifs,
      });
      onlineEl = buildOnlineEl();
      deliveryBody.appendChild(renderProjectSection(t('card.section.online'), onlineEl));
    }
    // Carte unifiée Task 8 — « 🛠 construire en local » section.
    // Both platform branches fill buildLocalBody; it is wrapped under a shared
    // section header and appended to deliveryBody only when it has content
    // (web without forgejo_repo_url → empty → section omitted).
    const buildLocalBody = document.createElement('div');
    buildLocalBody.className = 'project-section__build-local-body';

    // ── Shared delivery helpers (clone-first deliver + recover both use them) ──
    // Record the chosen dir on the row + persist + inject backend creds. The
    // .env.local write is idempotent (the scaffold already writes it ; the clone
    // needs it since the repo omits secrets).
    const applyDeliveredDir = async (path: string): Promise<void> => {
      row.local_dir = path;
      const cached = projectsCache.find((p) => p.id === row.id);
      if (cached) cached.local_dir = path;
      try { await setLocalDir(row.id, path); } catch (e) { console.warn('[delivery] setLocalDir failed', e); }
      if (row.backend_url && row.backend_anon_key) {
        try { await writeProjectEnv(path, row.backend_url, row.backend_anon_key); }
        catch (e) { console.warn('[delivery] writeProjectEnv failed', e); }
      }
    };
    // Recovery (on divergence) : re-clone the cloud HEAD into a freshly-picked
    // folder. Non-destructive — nothing local is overwritten (spec §6/§8 : the
    // diverged copy stays, the cloud version comes down clean). Opens the
    // recovered code WITHOUT an auto-rebuild.
    const recoverFromCloud = async (): Promise<void> => {
      const repo = await ensureProjectRepo();
      if (!repo?.cloneToken) return;
      const out = await cloneProject(row.title, repo.cloneUrl, repo.cloneToken);
      if (out.cancelled || !out.path) return;
      await applyDeliveredDir(out.path);
      mountDeliveredAgent(out.path, false);
    };
    // Plan-2 resume: clone the project's repo into a fresh folder, read its .genesis/
    // carrier, and hydrate the local stores — one observable action for the Plan-3
    // "Reprendre sur cette machine" button. Non-destructive; never throws.
    const resumeProjectOnThisMachine = async (emit?: (r: ResumeResult) => void): Promise<ResumeResult> => {
      // Instant feedback BEFORE the (network) ensureProjectRepo — otherwise a slow
      // or failing bootstrap shows nothing (the « bouton vert, rien ne se passe » bug).
      emit?.({ ok: false, step: 'cloning', localDir: null, message: '' });
      const repo = await ensureProjectRepo();
      if (!repo) {
        // ensureProjectRepo returned null → bootstrapForgejoRepo failed (e.g. the
        // project id isn't a cloud project for this account → project_not_found 404,
        // or a network error). EMIT it so the failure is never silent.
        const r: ResumeResult = { ok: false, step: 'failed', localDir: null, message: "Ce projet n'est pas relié au cloud sur ce compte — récupère d'abord tes projets (« Récupérer mes projets »), puis réessaie." };
        emit?.(r);
        return r;
      }
      const res = await resumeOnThisMachine(
        { localProjectId: row.id, title: row.title, cloneUrl: repo.cloneUrl, cloneToken: repo.cloneToken },
        {
          clone: (title, cloneUrl, cloneToken) =>
            cloneToken ? cloneProject(title, cloneUrl, cloneToken) : Promise.resolve({ cancelled: false, path: null, detail: 'Token de clonage manquant — réessayez après une synchronisation.' }),
          readGenesis: readGenesisFromDir,
          stores: realHydrationStores(),
          emit,
        },
      );
      if (res.ok && res.localDir) {
        row.local_dir = res.localDir;
        const cached = projectsCache.find((p) => p.id === row.id);
        if (cached) cached.local_dir = res.localDir;
        // The caller (resume panel) re-renders the whole card so the now-delivered
        // path mounts the agent — don't double-mount into the detached cloud body.
      }
      return res;
    };
    // Mount the agent panel for a delivered/cloned/recovered dir, wired for
    // cloud-save (auto-push + manual save + divergence recovery).
    const mountDeliveredAgent = (path: string, autoStart: boolean): void => {
      const target = cloudSaveTarget();
      mountAgentPanel(buildLocalBody, path, autoStart, buildAgentBuildTask(row, t, email), deliveryProvisionForRow(),
        target ? { ...target, onRecover: () => { void recoverFromCloud(); } } : undefined);
    };

    if (row.forgejo_repo_url) {
      // The Forgejo clone handoff is a WEB-only flow. On desktop the project is
      // delivered + built LOCALLY (the deliver button + agent panel below), so we
      // never ask the user to clone from the login-walled Forgejo — no SSH key and
      // no token setup per user. cf gitclone "automatic for all users" (volet ①).
      if (detectPlatform() !== 'desktop') {
        buildLocalBody.appendChild(renderTerminalHandoff({ repoUrl: row.forgejo_repo_url, cloneToken: row.forgejo_clone_token }));
      }
    }
    if (detectPlatform() === 'desktop') {
      if (row.local_dir) {
        // ALREADY delivered (path remembered) — don't re-offer "create" (that made
        // duplicate folders). Show the agent panel for the existing folder; no
        // auto-rebuild on a plain re-open.
        const where = document.createElement('p');
        where.className = 'project-preview-card__delivered-at';
        where.textContent = t('delivery.alreadyAt').replace('{path}', row.local_dir);
        buildLocalBody.appendChild(where);
        mountDeliveredAgent(row.local_dir, false);
      } else if (row.forgejo_repo_url) {
        // CLOUD state — the project lives in the cloud, not on this machine yet.
        // The prominent resume panel LEADS the delivery strip (renderResumePanel)
        // instead of a small badge buried under « construire en local ». « ⬇ Reprendre
        // sur ce poste » clones + hydrates .genesis/ (fiche+versions+sessions+backend)
        // here with observable progress (plain message + tucked technical detail) ;
        // on success the card RE-RENDERS into its delivered/agent state. The adopt
        // escape hatch + the « Récupérer vs Reprendre » legend live inside the panel.
        // It never scaffolds a divergent local copy over an existing cloud repo.
        const panel = renderResumePanel({
          locale,
          onResume: async () => {
            // Guard against a double-click firing two concurrent clones into two
            // folders (the clone is slow + network-bound). Disabled until the run
            // ends; re-enabled only on a failed/cancelled run (success re-renders).
            const cta = panel.querySelector<HTMLButtonElement>('.resume-panel__cta');
            if (cta?.disabled) return;
            if (cta) cta.disabled = true;
            const host = panel.querySelector<HTMLElement>('.resume-panel__progress');
            const res = await resumeProjectOnThisMachine((r) => {
              host?.replaceChildren(renderResumeProgress(r, { locale }));
            });
            if (res.ok && res.localDir && card.isConnected) {
              const next = renderEnrichedCardForRow(row, result, undefined, isExpanded);
              card.replaceWith(next);
              scrollCardIntoView(next);
            } else if (cta) {
              cta.disabled = false;
            }
          },
          onAdopt: async () => {
            const ghost = panel.querySelector<HTMLButtonElement>('.resume-panel__ghost');
            const msg = panel.querySelector<HTMLElement>('.resume-panel__adopt-msg');
            if (ghost) ghost.disabled = true;
            if (msg) msg.hidden = true;
            try {
              const { owner, repo_name } = deriveForgejoUrls(row.forgejo_repo_url!);
              const out = await adoptProjectFolder(`${owner}/${repo_name}`);
              if (out.cancelled) { if (ghost) ghost.disabled = false; return; }
              if (!out.path || !out.valid) {
                if (msg) { msg.textContent = t('delivery.adoptMismatch'); msg.hidden = false; }
                if (ghost) ghost.disabled = false; return;
              }
              await applyDeliveredDir(out.path);
              if (card.isConnected) {
                const next = renderEnrichedCardForRow(row, result, undefined, isExpanded);
                card.replaceWith(next);
                scrollCardIntoView(next);
              }
            } catch (e) {
              console.warn('[delivery] adopt failed', e);
              if (ghost) ghost.disabled = false;
            }
          },
        });
        // Lead the strip: insert before any « en ligne » section (present only when
        // the project is live-deployed) so « Reprendre » stays the first thing seen.
        deliveryBody.insertBefore(panel, deliveryBody.firstChild);
      } else {
        // FRESH project (no cloud repo yet) — scaffold/bootstrap locally.
        const deliver = document.createElement('button');
        deliver.type = 'button';
        deliver.className = 'project-preview-card__deliver';
        deliver.setAttribute('data-component', 'deliver-local');
        deliver.textContent = t('delivery.cta');
        deliver.title = t('delivery.hint');
        deliver.addEventListener('click', async () => {
          const idle = t('delivery.cta');
          deliver.disabled = true;
          deliver.textContent = t('delivery.busy');

          // Record the chosen dir + start the 0-friction auto-build (clone-first
          // and scaffold-fallback paths both land here).
          const onDelivered = async (path: string): Promise<void> => {
            await applyDeliveredDir(path);
            deliver.textContent = t('delivery.done');
            deliver.disabled = true;
            mountDeliveredAgent(path, true);
          };

          // Fallback : the original local scaffold (no cloud backing). Used when
          // the repo can't be ensured/cloned so the user is NEVER blocked from
          // building. Returns false if the user cancelled the folder picker.
          const scaffoldFallback = async (): Promise<boolean> => {
            const files = buildDeliveryFiles(projectRowToMagnifyResult(row), row.idea, locale, row.backend_url, row.backend_anon_key);
            const path = await deliverProject(row.title, files);
            if (!path) return false;
            await onDelivered(path);
            return true;
          };

          try {
            // Clone-first : pull the bootstrapped repo so the user works on the
            // REAL code + history (builds then auto-push fast-forward, and the
            // code follows across workstations). Degrade to a local scaffold on
            // any failure — honest, never blocking.
            const repo = await ensureProjectRepo();
            if (repo?.cloneToken) {
              const out = await cloneProject(row.title, repo.cloneUrl, repo.cloneToken);
              if (out.cancelled) { deliver.disabled = false; deliver.textContent = idle; return; }
              if (out.path) { await onDelivered(out.path); return; }
              console.warn('[delivery] clone failed, scaffolding locally', out.status, out.detail);
            }
            const ok = await scaffoldFallback();
            if (!ok) { deliver.disabled = false; deliver.textContent = idle; }
          } catch (err) {
            console.warn('[delivery] failed', err);
            deliver.textContent = t('delivery.error');
            setTimeout(() => { deliver.disabled = false; deliver.textContent = idle; }, 3000);
          }
        });
        buildLocalBody.appendChild(deliver);
      }
    }
    // Mount the « construire en local » section only when it has content.
    // Web with no forgejo_repo_url yet → buildLocalBody is empty → section omitted
    // (avoids an orphan section header). Desktop always has content (deliver CTA
    // or already-delivered panel). Web with forgejo_repo_url → handoff → content present.
    if (buildLocalBody.childElementCount > 0) {
      deliveryBody.appendChild(renderProjectSection(t('card.section.buildLocal'), buildLocalBody));
    }
    // Carte unifiée Task 5 — emplacement line: one <p> summarising where the
    // project lives (online URL, local folder, or "not yet delivered").
    // Mounted once per render at the bottom of the delivery body so both
    // platforms see the same structural slot in a predictable position.
    // Hidden in cloud-resume state — the resume panel already says where it lives —
    // unless the project is live-deployed, where the line carries the live URL.
    if (!isCloudResume || isLiveDeployed) {
      const loc = document.createElement('p');
      loc.className = 'project-preview-card__location';
      if (row.deploy_status === 'live' && row.deploy_url) {
        loc.textContent = t('card.location.online').replace('{url}', row.deploy_url);
      } else if (detectPlatform() !== 'desktop') {
        loc.textContent = t('card.location.soon');
      } else if (row.local_dir) {
        loc.textContent = t('card.location.local').replace('{path}', row.local_dir);
      } else {
        loc.textContent = t('card.location.notDelivered');
      }
      deliveryBody.appendChild(loc);
    }

    // Async fetch on first mount for this project (only if cache empty).
    if (cachedVersions.length === 0) {
      void refreshVersionsCache(row.id).then(() => {
        // Re-render WITHOUT the regen banner (the chip just appeared, not a
        // regen event) once versions are in cache — preserving the current
        // expand state so the chip landing doesn't snap the fiche shut/open.
        if (card.isConnected) {
          const next = renderEnrichedCardForRow(row, result, undefined, isExpanded);
          card.replaceWith(next);
        }
      });
    }
    return card;
  };

  // Helper : refresh the versions cache for a project from the DB. Called
  // after every regen so the chip count + tooltip stay accurate.
  const refreshVersionsCache = async (projectId: string): Promise<void> => {
    try {
      const versions = await data.listProjectVersions(projectId);
      versionsCache.set(projectId, versions);
    } catch (err) {
      console.warn('[welcome] listProjectVersions failed', err);
    }
  };

  // Helper : sync a fresh MagnifyResult into the cached ProjectRow so a
  // subsequent Mes-Projets re-open shows the current version without a
  // full row re-fetch.
  const applyResultToRow = (row: ProjectRow, result: MagnifyResult): void => {
    row.title = result.title;
    row.outline = result.outline;
    row.first_steps = result.first_steps;
    row.stack = result.stack ?? null;
    const cached = projectsCache.find((p) => p.id === row.id);
    if (cached) {
      cached.title = result.title;
      cached.outline = result.outline;
      cached.first_steps = result.first_steps;
      cached.stack = result.stack ?? null;
      cached.current_version_number = row.current_version_number;
    }
  };

  // Helper : smooth-scroll the replaced card into view so the user
  // visually confirms the regen landed. Operator-stated bug
  // (2026-05-17) : "on ne voit pas ce nouveau contenu" — root cause
  // was the card replacing in place at the same DOM position but
  // potentially below the fold. scrollIntoView with block:'start'
  // brings it to the top of the viewport.
  const scrollCardIntoView = (el: HTMLElement): void => {
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Alias kept for the Mes-Projets onSelect call site readability.
  // v7.8.1 (2026-05-29) — opening a project from Mes Projets shows the FULL
  // plan (outline + first_steps) by default. The ✦ ta fiche chrome stays
  // (pinned header + déplier/replier toggle + actions) ; only the initial
  // state flips from "collapsed pinned summary" to "expanded with the plan
  // détaillé visible underneath". The user can still collapse it manually.
  const renderProjectPreviewCardForRow = (row: ProjectRow): HTMLElement =>
    renderEnrichedCardForRow(row, undefined, undefined, true);

  // Tear down any live composer paste listener before wiping the region.
  // renderDropZone registers a document-level paste handler and exposes
  // __unsubscribePaste on its element; composer.ts proxies it to the
  // composer root so we can clean up from a single querySelector here.
  const clearRegion = (): void => {
    // The chat-first entry mounts no composer in `region` (the discovery chat
    // lives in chatBandHost); the legacy magnify-composer paste-teardown is gone.
    region.innerHTML = '';
  };

  // Desktop-only sync bar (P3/v2): a visible "Récupérer mes projets" affordance.
  // The Atelier already auto-pulls on load, but it's silent — this surfaces the
  // result (projects · versions · attachments) and lets the operator re-trigger.
  // Only rendered on desktop; on web it's a no-op (returns null → nothing mounts).
  const renderDesktopSyncBar = (): HTMLElement | null => {
    if (detectPlatform() !== 'desktop') return null;
    const bar = document.createElement('div');
    bar.className = 'desktop-sync-bar';
    bar.setAttribute('data-component', 'desktop-sync-bar');
    const status = document.createElement('span');
    status.className = 'desktop-sync-bar__status';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dashboard-cloud-sync';
    btn.setAttribute('data-component', 'cloud-sync');
    const idle = t('cloudSync.recoverCta');
    btn.textContent = idle;
    void supabase.auth.getSession().then(({ data }) => {
      const email = data.session?.user?.email;
      status.textContent = email ? `${t('cloudSync.connectedPrefix')}${email}` : '';
    });
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = t('cloudSync.busy');
      try {
        const res = await pullDesktopFromCloud();
        // Refresh the sync cache so the per-card badges reflect the new sync state
        // (the init snapshot is now stale: this pull may have added mappings + a
        // fresh lastSyncedAt). renderIdle() below repaints with the updated cache.
        try { [syncMapCache, lastSyncedAtCache] = await Promise.all([loadSyncMap(), getLastSyncedAt()]); }
        catch (e) { console.warn('[welcome] sync cache refresh failed (non-fatal):', e); }
        btn.textContent = (res.projects + res.versions + res.versionsPushed + res.attachments + res.backends + res.sessions === 0)
          ? t('cloudSync.nothingNew')
          : t('cloudSync.doneV2')
              .replace('{projects}', String(res.projects))
              .replace('{versions}', String(res.versions))
              .replace('{attachments}', String(res.attachments))
              .replace('{backends}', String(res.backends))
              .replace('{sessions}', String(res.sessions));
        projectsCache = await getDataBridge().listProjects();
        // Preserve a live chat if the operator clicks « Récupérer » mid-discovery:
        // refresh the projects list, but only rebuild the entry when idle.
        if (isIdleEntry()) renderIdle();
        else notifyDataChange();
      } catch (err) {
        console.warn('[cloud-sync] recover failed', err);
        btn.disabled = false;
        btn.textContent = t('cloudSync.error');
        setTimeout(() => { btn.textContent = idle; }, 3000);
      }
    });
    // Helper text clarifying « Récupérer mes projets » brings the LIST of cards
    // (the fiches), not the code — distinct from the per-project « Reprendre sur ce
    // poste » action. Without this the recoverWhat/recoverHelp strings were dead.
    const txt = document.createElement('div');
    txt.className = 'desktop-sync-bar__txt';
    const what = document.createElement('div');
    what.className = 'desktop-sync-bar__what';
    what.textContent = t('cloudSync.recoverWhat');
    const help = document.createElement('div');
    help.className = 'desktop-sync-bar__help';
    help.textContent = t('cloudSync.recoverHelp');
    txt.append(what, help);
    bar.append(btn, txt, status);
    return bar;
  };

  // Shared converse turn — used by the post-magnify refine (renderIdle) AND the
  // open-project chat (mountConverseChatForRow). On a material change it bumps
  // the version, refreshes the version cache + cached row (so the fiche chip /
  // ‹ prev arrow / projects list stay consistent — mirrors the regen path), and
  // re-renders the fiche in resultHost ONLY if that project is still the active
  // view (`openGen` guard — a turn whose project was navigated away from must not
  // clobber the now-open project's fiche).
  interface ConverseState { convId?: string; convManifest?: MagnifyResult; convVersion?: number; }
  const runConverseTurn = async (
    row: ProjectRow, st: ConverseState, message: string,
  ): Promise<ChatTurnResult> => {
    const gen = openGen;
    const r = await ai.converse(row.id, message, locale, st.convId, st.convManifest, st.convVersion);
    st.convId = r.conversation_id;
    if (r.manifest_changed && r.manifest) {
      st.convVersion = r.version_number;
      st.convManifest = r.manifest;
      if (typeof r.version_number === 'number') row.current_version_number = r.version_number;
      await refreshVersionsCache(row.id); // the converse edge fn persisted a 'chat' version
      applyResultToRow(row, r.manifest);  // keep row + projectsCache in sync (title/outline/…)
      const ficheHost = regionOpts.resultHost;
      if (ficheHost && gen === openGen) {
        const next = renderEnrichedCardForRow(row, r.manifest, undefined, true);
        ficheHost.innerHTML = '';
        ficheHost.appendChild(next);
      }
    }
    return { reply: r.reply, ficheVersion: r.manifest_changed ? r.version_number : undefined };
  };

  // Chat for an already-started project (operator 2026-06-22): the same band,
  // bound to converse from turn 1 — no discovery phase, no Magnifier (the idea
  // is already magnified). chatStarted=true protects the open view from the
  // automatic renderIdle refreshes (entitlement / desktop pull / syncmap).
  const mountConverseChatForRow = (row: ProjectRow): void => {
    const host = regionOpts.chatBandHost;
    if (!host) return;
    openGen += 1; // a new active view — invalidates any in-flight converse turn
    host.hidden = false;
    host.innerHTML = '';
    chatStarted = true;
    const st: ConverseState = {
      convManifest: projectRowToMagnifyResult(row),
      convVersion: row.current_version_number,
    };
    const chatEl = renderDiscoveryChat({
      locale,
      // Function replacer: a title with `$` patterns (e.g. "P&L $5") must not be
      // reinterpreted by String.replace's special replacement syntax.
      greeting: t('discoveryChat.resumeGreeting').replace('{title}', () => row.title),
      cap: t('discoveryChat.resumeCap'),
      onTurn: (message) => runConverseTurn(row, st, message),
      onMagnify: async () => {}, // never invoked (onTurn never returns `ready`)
    });
    host.appendChild(chatEl);
  };

  const renderIdle = (): void => {
    openGen += 1; // a fresh entry view — invalidates any in-flight converse turn
    showDashboardRail(); // restore the dashboard rail (leaving any open fiche)
    clearRegion();
    // Desktop: visible cloud-recovery bar above the composer (no-op on web).
    const syncBar = renderDesktopSyncBar();
    if (syncBar) region.appendChild(syncBar);
    // ── Chat-first discovery entry (operator brief 2026-06-21) ───────────────
    // The conversation lives in the full-width top band; magnify becomes the
    // PAYOFF at the end of a discovery exchange, not the entry. The chat drives
    // the SAME magnify machinery the composer used to own — extracted here into
    // reusable helpers (quota / 403 keyless fallback / auto-save / fiche reveal).
    const magnifyOrBootstrap = async (idea: string, context?: string): Promise<ComposerOutcome> => {
      currentIdea = idea;
      try {
        // Pass the discovery transcript as attachments_context so the fiche comes
        // out calé on the conversation (same channel as the doc-discovery Q&A).
        const result = await ai.magnify(idea, locale, context);
        return { kind: 'magnified' as const, result };
      } catch (err) {
        const status = (err as { status?: number } | null)?.status;
        if (isQuotaError(err) || status === 402) { showQuotaExhausted(); throw err; }
        if (status !== 403) throw err; // genuine error — never mask a real outage
        // 403 ai_not_configured : keyless fallback — seed locally + push to Forgejo
        // so the user can magnify with their OWN Claude via terminal.
        try {
          const row = await getDataBridge().saveProjectSeed(idea, locale);
          projectsCache = [row, ...projectsCache];
          entitlementCache = {
            ...entitlementCache,
            ideasConsumed: entitlementCache.ideasConsumed + 1,
            remaining: Math.max(0, entitlementCache.freeQuota + entitlementCache.purchasedCredits - (entitlementCache.ideasConsumed + 1)),
          };
          const repo = await bootstrapForgejoRepo(row.id, context);
          return { kind: 'bootstrapped' as const, repo };
        } catch (seedErr) {
          if (isQuotaError(seedErr)) showQuotaExhausted();
          throw seedErr;
        }
      }
    };

    // Mounts the magnify outcome (fiche card or bootstrap next-steps) exactly as
    // the composer's onSuccess did; fires onSaved with the persisted row so the
    // chat can switch to converse mode against the real project id.
    const revealOutcome = (
      outcome: ComposerOutcome,
      onSaved?: (row: ProjectRow) => void,
      // True iff the magnify view is still the active one. When a magnify is in
      // flight and the user opens a saved project, openGen bumps and this turns
      // false — we then save the idea but never repaint over the open project.
      shouldRender: () => boolean = () => true,
    ): void => {
      const host = regionOpts.resultHost ?? region;
      const useHost = regionOpts.resultHost !== undefined;
      const mountResult = (el: HTMLElement, init?: { result?: MagnifyResult; idea?: string }): void => {
        if (!shouldRender()) return; // navigated away mid-magnify — don't clobber the now-open view
        regionOpts.onActivate?.(init);
        if (useHost) { host.hidden = false; host.innerHTML = ''; host.appendChild(el); }
        else { clearRegion(); region.appendChild(el); }
      };
      if (outcome.kind === 'bootstrapped') {
        mountResult(renderForgejoNextSteps({ htmlUrl: outcome.repo.html_url, cloneToken: outcome.repo.clone_token }));
        // The keyless seed was already pushed into projectsCache by
        // magnifyOrBootstrap; refresh the picker so it shows (esp. when navigated
        // away — the next-steps card is skipped but the project must still list).
        notifyDataChange();
        return;
      }
      const result = outcome.result;
      const renderInitial = (): HTMLElement =>
        renderProjectPreviewCard({ result, idea: currentIdea, onRegenerate: () => renderIdle(), initialSaved: true });
      const renderSaved = (row: ProjectRow): HTMLElement => renderEnrichedCardForRow(row, undefined, undefined, true);
      const card = renderInitial();
      mountResult(card, { result, idea: currentIdea });
      getDataBridge().saveProject(result, currentIdea, locale)
        .then((row) => {
          projectsCache = [row, ...projectsCache];
          entitlementCache = {
            ...entitlementCache,
            ideasConsumed: entitlementCache.ideasConsumed + 1,
            remaining: Math.max(0, entitlementCache.freeQuota + entitlementCache.purchasedCredits - (entitlementCache.ideasConsumed + 1)),
          };
          // Only repaint the saved card if it is still the active view, else
          // renderSaved's shared-host side effects would clobber the open project.
          if (shouldRender()) {
            const next = renderSaved(row);
            card.replaceWith(next);
          }
          notifyDataChange();
          onSaved?.(row);
        })
        .catch((err) => {
          if (isQuotaError(err)) { showQuotaExhausted(); return; }
          console.warn('[welcome] auto-save failed, falling back to manual Save button:', err);
          if (!shouldRender()) return; // navigated away — don't mount a retry card over the open project
          const retry = renderProjectPreviewCard({
            result, idea: currentIdea, onRegenerate: () => renderIdle(),
            onSave: async () => {
              const row = await getDataBridge().saveProject(result, currentIdea, locale);
              projectsCache = [row, ...projectsCache];
              const next = renderSaved(row);
              retry.replaceWith(next);
              onSaved?.(row);
            },
            initialSaved: false,
          });
          card.replaceWith(retry);
        });
    };

    // Mount the chat into the full-width top band. Genesis asks discovery
    // questions (GuidedDiscussion); the Magnifier CTA appears once the exchange
    // is rich enough → magnify(transcript) → fiche reveals below. The SAME chat
    // then continues via converse to refine the saved fiche.
    const chatBandHost = regionOpts.chatBandHost;
    if (chatBandHost) {
      chatBandHost.hidden = false; // un-hide if a prior open-project hid it
      chatBandHost.innerHTML = '';
      chatStarted = false; // a fresh entry chat — no conversation yet
      const discussion = createGuidedDiscussion();
      let discState: DiscussionState | undefined;
      let started = false;
      let magnifyDone = false; // set once magnify commits — guards a 2nd Magnifier run
      let convRow: ProjectRow | undefined; // set once the fiche is saved
      const convState: ConverseState = {};
      // Guided first-run: pre-magnify dropped files buffer here (no project id yet)
      // and upload once the project is saved — best-effort, never blocks the flow.
      const pendingFiles: File[] = [];
      const uploadPendingFiles = async (row: ProjectRow, files: File[]): Promise<void> => {
        const store = getAttachmentBridge();
        for (const f of files) {
          try { await store.upload(row.user_id, row.id, f); }
          catch (e) { console.warn('[welcome] pending file upload failed (non-fatal):', e); }
        }
        notifyDataChange();
      };

      const chatEl = renderDiscoveryChat({
        locale,
        greeting: t('discoveryChat.greeting'),
        remaining: entitlementCache.remaining,
        // incr.4a §5.7 — lazy: the guidedRun latch lands below in this same
        // render pass (see the latch block), after this chat is constructed.
        guided: () => guidedRun,
        onTurn: async (message) => {
          // Post-magnify : refine the saved fiche via converse (shared helper —
          // re-renders the fiche in resultHost on a material change).
          if (convRow) return runConverseTurn(convRow, convState, message);
          // Pre-magnify : guided discovery Q&A (no network, no quota spent).
          if (!started) {
            started = true;
            chatStarted = true; // protect this conversation from automatic renderIdle refreshes
            const s = discussion.start(message, locale);
            discState = s.state;
            return { reply: s.turn.reply, ready: s.turn.ready };
          }
          const s = discussion.respond(discState!, message, locale);
          discState = s.state;
          return { reply: s.turn.reply, ready: s.turn.ready };
        },
        onMagnify: async () => {
          if (magnifyDone || !discState) return; // one magnify per conversation
          const el = chatEl as DiscoveryChatElement;
          // Capture the active-view generation: if the user opens a saved project
          // while this magnify is in flight (the picker tiles are not locked),
          // openGen bumps and `stillHere()` turns false — we then save the idea
          // (so it is not lost) but never repaint over the open project.
          const gen = openGen;
          const stillHere = () => gen === openGen;
          el.__setBusy?.(true); // lock input + send + CTA during magnify → save
          const context = discussion.toMagnifyContext(discState, locale);
          try {
            const outcome = await magnifyOrBootstrap(discState.idea, context);
            magnifyDone = true;
            el.__retireMagnify?.(); // the payoff is spent — refine via the chat now
            if (outcome.kind === 'bootstrapped') {
              // Keyless fallback : a seed repo, no converse target. The next-steps
              // card carries the action; the chat stays locked.
              revealOutcome(outcome, undefined, stillHere);
              return;
            }
            revealOutcome(outcome, (row) => {
              if (!stillHere()) return; // navigated away — idea saved + listed, but
              // leave the open project's chat + fiche exactly as the user left them
              convRow = row;
              if (pendingFiles.length) void uploadPendingFiles(row, pendingFiles.splice(0));
              convState.convManifest = projectRowToMagnifyResult(row);
              convState.convVersion = undefined;
              el.__setBusy?.(false); // unlock — now in converse mode against the saved fiche
              el.__appendBot?.(t('discoveryChat.wrap'));
            }, stillHere);
          } catch (err) {
            magnifyDone = false; // genuine failure — allow a retry
            el.__setBusy?.(false);
            // Quota (402 / P0001) already surfaced via showQuotaExhausted (which
            // re-renders the chat with the /tarifs CTA). For a genuine error, keep
            // the transcript and tell the user in the chat so they can retry.
            const status = (err as { status?: number } | null)?.status;
            if (!isQuotaError(err) && status !== 402) {
              el.__appendBot?.(t('magnify.errorGeneric'));
            }
          }
        },
      });
      // 2026-06-23 guided first-run (v3, operator-approved): the deposit-box LEADS
      // the entry — write OR glisse/colle/lier un dossier — and feeds the SAME
      // discovery chat (text → __startWith) + buffers pre-magnify files. Gated by
      // isGuidedFirstRun + fully guarded: non-guided / dismissed / any throw →
      // today's chat-only entry (guidance is additive, never load-bearing — spec §9).
      let guided = false;
      try {
        guided = isGuidedFirstRun({
          projectCount: projectsCache.length,
          hasCompletedMagnify: entitlementCache.ideasConsumed > 0,
          dismissed: isGuidanceDismissed(),
        });
      } catch { guided = false; }
      guided = guided || forceGuided; // dogfood hatch overrides graduation/dismissal
      // Latch the guided run so the post-magnify card stays guided (see guidedRun
      // declaration). Only latch with REAL loaded data + a genuine blank slate
      // (0 projects) — the empty-cache first paint must not guide a returning user
      // before their projects load. Never un-latch within a run except on dismissal.
      if (forceGuided) {
        guidedRun = true;
      } else {
        if (projectsLoaded && projectsCache.length === 0 && !isGuidanceDismissed()) guidedRun = true;
        if (isGuidanceDismissed()) guidedRun = false;
      }
      // incr.4b §5.8 — slim the ambient chrome for the guided run (Appendix D.1).
      // The header is a full-bleed SIBLING of .welcome-page (renderWelcomePage
      // scope), so resolve both roots by selector — /welcome is the only page
      // mounted while this region renders.
      applyGuidedChrome({
        header: document.querySelector('.site-header'),
        page: document.querySelector('[data-component="welcome-page"]'),
      }, guidedRun);
      if (guided) {
        try {
          const gfrWrap = document.createElement('div');
          gfrWrap.className = 'gfr';
          gfrWrap.appendChild(renderDepositBox({
            onTextSubmit: (text) => { if (text) (chatEl as DiscoveryChatElement).__startWith?.(text); },
            onFile: (f) => { pendingFiles.push(f); },
          }));
          chatBandHost.appendChild(gfrWrap);
        } catch (err) {
          console.warn('[welcome] guided deposit-box mount failed (non-fatal, chat-only fallback):', err);
        }
      }
      chatBandHost.appendChild(chatEl);
    }
  };

  // CP-1 Task 6: fire getEntitlement() alongside listProjects() so both are
  // available for the first re-render. Both are async; the page paints
  // synchronously with the safe defaults then re-renders once either resolves.
  // Failures are non-blocking (log only) — the safe defaults keep the page usable.
  Promise.all([
    getDataBridge().listProjects(),
    getEntitlement(),
  ]).then(([rows, entitlement]) => {
    projectsCache = rows;
    entitlementCache = entitlement;
    projectsLoaded = true;
    // Only refresh if we're still on the idle state (composer visible).
    // If the user has already started a Magnify call, leave them alone.
    if (isIdleEntry()) renderIdle();
    notifyDataChange(); // counter + composer now reflect the real values
    pullDesktopProjectsThenRefresh(); // P3: bring web-created ideas into the desktop Atelier
  }).catch((err) => {
    console.warn('[welcome] data load failed (non-fatal):', err);
    // Still try the individual fetches so partial data is shown.
    getDataBridge().listProjects()
      .then((rows) => {
        projectsCache = rows;
        projectsLoaded = true;
        if (isIdleEntry()) renderIdle();
        notifyDataChange();
        pullDesktopProjectsThenRefresh(); // P3: bring web-created ideas into the desktop Atelier
      })
      .catch((err2) => { console.warn('[welcome] listProjects failed:', err2); });
    getEntitlement()
      .then((entitlement) => {
        entitlementCache = entitlement;
        if (isIdleEntry()) renderIdle();
        notifyDataChange();
      })
      .catch((err2) => { console.warn('[welcome] getEntitlement failed:', err2); });
  });

  // Expose the region's renderIdle + open-row helpers up to the parent so
  // the welcome-sidebar callbacks (+ Nouvelle idée, project click) can drive
  // the canvas without piercing the region closure.
  regionOpts.setRenderIdle?.(renderIdle);
  regionOpts.exposeOpenRow?.(renderProjectPreviewCardForRow);
  regionOpts.exposeMountConverseChat?.(mountConverseChatForRow);

  renderIdle();
  notifyDataChange();
}

// 2.7.1 item #2 — delivery-view provision affordance. When the delivered project
// has no backend yet, the history col (LEFT, above the accordion) re-offers the
// "Activer la base de données" CTA so the user doesn't have to hunt for it in
// the collapsed fiche. Routes through the SAME wizard + handlers as the card
// backend slot (project-preview-card wireEmptyHandlers).
interface DeliveryProvision {
  hasBackend: boolean;
  title: string;
  onProvisionBackend: (args: { handle?: string; model: BaseModel }) => Promise<unknown>;
  onCloudUpsell?: () => void;
  onPollStatus?: MiseEnLigneWizardOptions['onPollStatus'];
  onDeployFrontend?: (args: { handle: string; model: BackendModel }) => Promise<DeployOutcome>;
}

// Desktop agent panel (mounted after a project is delivered locally). Claude Code
// runs IN the delivered folder and STREAMS its work into a narrated activity feed
// (claude_agent_stream → Tauri Channel → parseClaudeStreamLine → renderAgentPanel),
// replacing the old blocking call that froze for minutes ("pas pilotable en auto").
// On a FRESH delivery it auto-starts the build ; on a plain re-open it just waits
// for a follow-up (no surprise rebuild). The input lets the user ask for more.
function mountAgentPanel(host: HTMLElement, projectDir: string, autoStart = true, buildTask?: string, provision?: DeliveryProvision, cloudSave?: { projectId: string; repoUrl: string; onRecover?: () => void }): void {
  host.querySelector('[data-component="agent-panel"]')?.remove();
  // Plan-usage throttle-card host : sits above the agent panel, populated when a
  // run ends throttled. Removed on remount like the panel (last-mount-wins).
  host.querySelector('[data-component="usage-throttle-host"]')?.remove();
  const throttleHost = document.createElement('div');
  throttleHost.setAttribute('data-component', 'usage-throttle-host');
  host.prepend(throttleHost);
  // Last-mount-wins (review fix C1): deterministically detach the previous
  // mount's display subscription + timer for this dir before wiring ours.
  agentPanelTeardowns.get(projectDir)?.();
  const mgr = agentRunManager();

  // ── Code-continuity cloud-save : after each run (and on a manual "save now")
  // the built code is pushed back to the project's Forgejo repo so it follows
  // the user across workstations. T7′ owns the ORCHESTRATION (token mint → push
  // → honest state) ; the visual indicator is the /frontend-design component
  // (T8′) that renderCloudSave() mounts into cloudSaveHost. NON-BLOCKING : the
  // build never waits on the push, and a push failure only flips the state.
  const cloudSaveHost = document.createElement('div');
  cloudSaveHost.setAttribute('data-component', 'cloud-save-host');
  let cloudSaveState: CloudSaveState = 'idle';
  // Render the /frontend-design indicator for the current state. onSave = manual
  // "save now" ; onRecover = non-destructive re-clone of the cloud HEAD into a
  // fresh folder (recoverFromCloud) — nothing local is overwritten.
  const renderCloudSave = (): void => {
    if (!cloudSave || cloudSaveState === 'idle') { cloudSaveHost.replaceChildren(); return; }
    cloudSaveHost.replaceChildren(renderCloudSaveIndicator(cloudSaveState, {
      onSave: () => triggerCloudSave(),
      onRecover: () => cloudSave.onRecover?.(),
    }));
  };
  const setCloudSaveState = (s: CloudSaveState): void => { cloudSaveState = s; renderCloudSave(); };
  // One cloud-save cycle (auto after a run, or manual). Non-blocking : callers
  // `void` it. Guarded to desktop + a known repo target.
  const triggerCloudSave = (): void => {
    if (!cloudSave || detectPlatform() !== 'desktop') return;
    void (async () => {
      // Write the .genesis/ carrier into the project dir FIRST so the code push
      // commits + pushes it alongside the code (one atomic commit).
      await saveGenesisForProject(projectDir, cloudSave.projectId, {
        getProject: async (id) => (await getDataBridge().listProjects()).find((p) => p.id === id) ?? null,
        getVersions: (id) => listVersionsForProject(id),
        getSessions: (dir) => loadSessions(dir),
        write: (dir, state) => writeGenesisToDir(dir, state),
      });
      await runCloudSave(
        { projectDir, projectId: cloudSave.projectId, repoUrl: cloudSave.repoUrl },
        setCloudSaveState,
        { mint: mintProjectWriteToken, push: pushProjectCode, now: () => new Date().toISOString() },
      );
      // Also back up the build-session history to the cloud DB on EVERY build-end
      // (web visibility + a 2nd backup beside the repo carrier). This closes the gap
      // that kept public.project_sessions empty in prod: the DB push used to fire
      // only on the rarely-clicked « Récupérer mes projets ». Guarded + non-blocking.
      try {
        const sess = await loadSessions(projectDir);
        if (sess.length > 0) await pushSessionsForProject(cloudSave.projectId, sess);
      } catch { /* never block the build — pushSessionsForProject already swallows */ }
    })();
  };

  // Display-side elapsed timer (UI chrome). The BASE timestamp lives in the run
  // manager (getStartedAt) so a re-attached panel resumes the correct elapsed.
  // The interval self-stops when the panel leaves the DOM.
  let timer: ReturnType<typeof setInterval> | undefined;
  const stopTimer = () => { if (timer) { clearInterval(timer); timer = undefined; } };
  const startTimer = (startedAtMs: number) => {
    stopTimer();
    let sawConnected = false;
    const tick = () => {
      if (handle.el.isConnected) sawConnected = true;
      else if (sawConnected) { stopTimer(); return; } // panel torn down → stop ticking
      handle.setElapsed(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)));
    };
    tick();
    timer = setInterval(tick, 1000);
  };

  // 2.5.8 — persist build history as SESSIONS (one Claude conversation thread per
  // session; follow-ups use --continue). The session-boundary policy decides when to
  // start fresh (app-open OR manual "Nouvelle session" OR the per-session request cap).
  // 2.7.1 item C — the in-memory sessions are SHARED across mounts (module-level
  // cache) and the line capture + debounced persist are OWNED BY THE RUN MANAGER
  // (registered per run at startRun), so both keep working while no panel is
  // mounted. The panel itself is display-only: it subscribes for live messages
  // and re-hydrates on remount.
  let sessions = agentSessionsCache.get(projectDir);
  if (!sessions) { sessions = []; agentSessionsCache.set(projectDir, sessions); }
  const sessionsRef = sessions; // non-undefined alias for the closures below
  let manualReset = false;
  // Desktop-only left-col session-history accordion (project → sessions → requests
  // → detail). Lives BELOW the live agent panel ; re-rendered from the in-memory
  // `sessions` on every persist so a fresh build / follow-up appears in the history.
  let historyHost: HTMLElement | undefined;
  const refreshHistory = () => {
    if (!historyHost) return;
    historyHost.replaceChildren(renderSessionHistory(projectDir, sessionsRef));
  };
  // Register as the project's CURRENT history refresher (last mount wins): the
  // manager's onPersist resolves it at call time so a run started by a previous
  // (now dead) panel refreshes THIS panel's accordion.
  agentHistoryRefreshers.set(projectDir, refreshHistory);

  // continueConv = true on a user FOLLOW-UP (the user asked for more) ; false on the
  // initial auto-build. The ACTUAL --continue decision is made by the policy: continue
  // only when the resulting session is NOT fresh.
  const runStreaming = (prompt: string, continueConv = false) => {
    const kind = continueConv ? 'followup' : 'build';
    const d = decideRun({
      current: sessionsRef.at(-1),
      isAppFirstRun: !appRunStarted.has(projectDir),
      manualReset,
      prompt,
      kind,
      now: new Date().toISOString(),
      newId: () => crypto.randomUUID(),
    });
    appRunStarted.add(projectDir);
    manualReset = false;

    if (d.fresh) {
      sessionsRef.push(d.session);
    } else {
      // decideRun returns a NEW session object (prior requests + the appended one);
      // swap it in place of the one it superseded.
      const i = sessionsRef.findIndex((s) => s.id === d.session.id);
      if (i >= 0) sessionsRef[i] = d.session; else sessionsRef.push(d.session);
    }
    const req = d.session.requests.at(-1)!;

    handle.beginRequest(prompt, d.fresh);
    startTimer(Date.now());
    // CAPTURE side — owned by the manager, survives this panel's unmount: the
    // raw-line append into the session request + the debounced persist (flushed
    // immediately on done/error). DISPLAY arrives via the subscription below.
    const usage = getUsageBridge();
    mgr.startRun(projectDir, prompt, {
      // Continue the conversation only when this run is NOT a fresh session.
      continueConversation: !d.fresh,
      onLine: (line) => { req.lines.push(line); usage.tapLine(line); },
      onPersist: () => {
        void saveSessions(projectDir, sessionsRef).catch(() => {});
        agentHistoryRefreshers.get(projectDir)?.();
      },
      // Auto-push the built code when the run ends — owned by the MANAGER so it
      // fires even if the panel was torn down mid-build (the code must reach
      // Forgejo regardless). Non-blocking ; honest state on the indicator.
      onEnd: () => { triggerCloudSave(); },
    });
  };

  const handle = renderAgentPanel({
    // A typed follow-up continues the conversation (memory) ; the auto-build starts fresh.
    onRun: (prompt) => runStreaming(prompt, true),
    onCancel: () => { mgr.cancel(projectDir); },
    onOpenTerminal: () => { void openTerminal(projectDir); },
    // "Nouvelle session" → the NEXT run starts fresh (no --continue).
    onNewSession: () => { manualReset = true; },
  });

  // ── DISPLAY-ONLY subscription (replay:false — the restore path below replays
  // from the richer session history, which already contains the buffered lines
  // through the shared in-memory session objects). Teardown is deterministic:
  // the NEXT mountAgentPanel for this dir calls the teardown registered below
  // (last-mount-wins). The wasConnected latch stays as a safety net for a panel
  // torn down by some path that never remounts one for this dir.
  let wasConnected = false;
  let restoring = false;
  const pendingWhileRestoring: AgentChannelMsg[] = [];
  // Plan-usage : when a run ends, surface the throttle card in the chemise if the
  // user hit their Claude 5 h wall. Honest-first (free resume), soft-upsell only on
  // repeat. Cleared when not throttled. Consent-gated like the diag gauge.
  const maybeShowThrottle = async (): Promise<void> => {
    try {
      if (!usageConsentGranted()) { throttleHost.replaceChildren(); return; }
      const state = await getUsageBridge().getCurrentState();
      if (state.status === 'throttled') {
        throttleHost.replaceChildren(renderInkGaugeCard(state, {
          showUpsell: usageThrottleRepeated(),
          onResume: () => { throttleHost.replaceChildren(); if (buildTask) runStreaming(buildTask, true); },
          onSwitchPlan: () => { window.location.assign('/connecter'); },
        }));
      } else {
        throttleHost.replaceChildren();
      }
    } catch { throttleHost.replaceChildren(); }
  };
  const display = (msg: AgentChannelMsg) => {
    if (msg.event === 'line') {
      handle.rawLine(msg.data.line);
      const ev = parseClaudeStreamLine(msg.data.line);
      if (ev) handle.push(ev);
    } else if (msg.event === 'error') {
      stopTimer();
      handle.fail(msg.data.message);
      void maybeShowThrottle();
      // (auto-push is wired on the manager's onEnd so it fires even when this
      // panel is detached — see runStreaming/startRun.)
    } else if (msg.event === 'done') {
      stopTimer(); // the `result` parsed event already rendered the outcome
      void maybeShowThrottle();
    }
  };
  const unsubscribe = mgr.subscribe(projectDir, (msg) => {
    if (handle.el.isConnected) {
      wasConnected = true;
    } else if (wasConnected) {
      // Panel torn down (navigation / re-render) → detach the display. The
      // manager keeps capturing + persisting; the next mount re-attaches.
      stopTimer();
      unsubscribe();
      return;
    }
    if (restoring) { pendingWhileRestoring.push(msg); return; }
    display(msg);
  }, { replay: false });
  // Re-review fix (C1, second half): a teardown marks this mount DEAD so its
  // restore IIFE — possibly still parked on the shared load promise below —
  // bails on resume instead of re-entering beginRequest/startTimer on a
  // detached panel. A timer armed while detached never trips the tick latch's
  // sawConnected → it would tick forever (zombie interval).
  let dead = false;
  agentPanelTeardowns.set(projectDir, () => { dead = true; stopTimer(); unsubscribe(); });

  // ── Delivery view: 1/3 session-history (LEFT) + 2/3 agent panel (RIGHT)
  // with a reserved full-width preview slot below (hidden until the preview
  // feature is built). This replaces the previous flat append of historyHost
  // + panel into the delivery body.
  const deliveryMain = document.createElement('div');
  deliveryMain.className = 'welcome-delivery__main';

  // LEFT column — session-history accordion
  const historyCol = document.createElement('div');
  historyCol.className = 'welcome-delivery__history-col';
  // 2.7.1 item #2 — provision CTA at the TOP of the history col when the project
  // has no backend yet. Mirrors the card backend slot's wireEmptyHandlers wiring
  // (same wizard, same gate-aware onConfirm routing); on success the CTA block
  // removes itself — row.backend_kind is mutated by the shared handler, so the
  // next render won't re-offer it.
  if (provision && !provision.hasBackend) {
    const ctaBlock = document.createElement('div');
    ctaBlock.className = 'welcome-delivery__provision';
    const ctaBtn = document.createElement('button');
    ctaBtn.type = 'button';
    ctaBtn.className = 'welcome-delivery__provision-btn';
    ctaBtn.setAttribute('data-component', 'delivery-provision-cta');
    ctaBtn.textContent = t('backend.genesisHostCta');
    const ctaHint = document.createElement('p');
    ctaHint.className = 'welcome-delivery__provision-hint';
    ctaHint.textContent = t('backend.genesisHostHint');
    ctaBlock.append(ctaBtn, ctaHint);
    ctaBtn.addEventListener('click', () => {
      const wizard = renderMiseEnLigneWizard({
        title: provision.title,
        onConfirm: async ({ handle, model }) => {
          // deploy_frontend (gate on): deploy the site (+ optional base) and return
          // the outcome so the wizard shows build-progress → live URL.
          if (deployFrontendEnabled() && provision.onDeployFrontend) {
            const out = await provision.onDeployFrontend({ handle, model });
            // A base was provisioned alongside the deploy unless "just the site".
            if (model !== 'none') ctaBlock.remove();
            return out;
          }
          // shipped-A: provision the base only; the wizard closes itself.
          const baseModel = model as BaseModel; // gate off → never 'none'
          await provision.onProvisionBackend({ handle, model: baseModel });
          ctaBlock.remove();
        },
        onPollStatus: provision.onPollStatus,
        onCloudUpsell: provision.onCloudUpsell,
      });
      document.body.appendChild(wizard);
    });
    historyCol.appendChild(ctaBlock);
  }
  const historyLabel = document.createElement('p');
  historyLabel.className = 'welcome-delivery__history-label';
  historyLabel.textContent = t('delivery.historyLabel');
  historyCol.appendChild(historyLabel);
  historyHost = document.createElement('div');
  historyHost.setAttribute('data-component', 'session-history-host');
  historyCol.appendChild(historyHost);
  deliveryMain.appendChild(historyCol);

  // RIGHT column — agent panel ("Claude construit ton projet")
  const agentCol = document.createElement('div');
  agentCol.className = 'welcome-delivery__agent-col';
  agentCol.appendChild(handle.el);
  // Cloud-save indicator slot (T8′ renders the /frontend-design component here).
  // Present from T7′ so the auto-push state has a stable mount point.
  agentCol.appendChild(cloudSaveHost);
  deliveryMain.appendChild(agentCol);

  host.appendChild(deliveryMain);

  // Reserved preview slot — hidden, full-width, for a future preview pane.
  const previewSlot = document.createElement('div');
  previewSlot.className = 'welcome-delivery__preview-slot';
  previewSlot.setAttribute('data-component', 'delivery-preview-slot');
  previewSlot.hidden = true;
  host.appendChild(previewSlot);

  if (autoStart) {
    // Fresh delivery (operator's "démarre tout seul").
    runStreaming(buildTask ?? t('delivery.agentBuildTask'));
  } else {
    // Plain re-open / RE-ATTACH after navigation → no rebuild. Reconstruct the
    // latest session's feed, and when a run is STILL IN FLIGHT re-enter the
    // running chrome + replay its captured-so-far lines, then go live.
    //
    // Race-safety: the snapshot below runs in the SAME synchronous segment as
    // the subscription above, so no channel message can land in between — the
    // snapshot (past) and the restoring-queue (live) are exactly complementary,
    // no line lost, none doubled.
    restoring = true;
    const running = mgr.isRunning(projectDir);
    const inFlightReq = running ? sessionsRef.at(-1)?.requests.at(-1) : undefined;
    const inFlightLines = inFlightReq ? [...inFlightReq.lines] : [];
    void (async () => {
      try {
        // Cold-load from the store, keyed by PROMISE (review fix I1): every
        // mount awaits the SAME read, so a remount during a pending load still
        // hydrates when it resolves. A rejected read clears the entry → the
        // next mount retries instead of trusting a poisoned-empty cache.
        let load = agentSessionsLoads.get(projectDir);
        if (!load) {
          load = loadSessions(projectDir).then((loaded) => {
            // Merge IN PLACE, never splice over (review fix I2): a run started
            // while the read was pending already lives in sessionsRef —
            // replacing the contents would orphan the in-flight session object
            // (onLine keeps appending into it, but the next flush would persist
            // the array WITHOUT it). Loaded PRIOR sessions go BEFORE the
            // in-memory ones, UNCONDITIONALLY (re-review I2 residual: gating
            // the prepend on isRunning dropped the store's history when a run
            // COMPLETED before this read resolved — the next flush then
            // overwrote the file without it). The by-id dedupe keeps in-memory
            // sessions authoritative for same-id entries. The array identity
            // is shared with capture closures — never swap it.
            const have = new Set(sessionsRef.map((s) => s.id));
            sessionsRef.splice(0, 0, ...loaded.filter((s) => !have.has(s.id)));
            // Route a refresh through the CURRENT mount's accordion — this
            // closure may have been created by an already-detached mount.
            agentHistoryRefreshers.get(projectDir)?.();
          });
          agentSessionsLoads.set(projectDir, load);
          load.catch(() => { agentSessionsLoads.delete(projectDir); });
        }
        try { await load; } catch { /* no stored history — next mount retries */ }
        // Superseded while parked on the load? Bail before any hydrate/display
        // work — and critically before beginRequest/startTimer (the zombie
        // interval). The finally below still clears `restoring` consistently.
        if (dead) return;
        // hydrate() is async+chunked — await it so refreshHistory() runs only after
        // the feed is fully populated (avoids a stale accordion on slow histories).
        const latest = sessionsRef.at(-1);
        // A request that went LIVE during this restore (the panel's own
        // runStreaming while the load was pending) displays through the pending
        // queue below — hydrating its lines too would double them.
        const liveReq = mgr.isRunning(projectDir) ? latest?.requests.at(-1) : undefined;
        const priorLines = latest ? latest.requests.flatMap((r) => (r === inFlightReq || r === liveReq ? [] : r.lines)) : [];
        if (priorLines.length) await handle.hydrate(priorLines);
        // hydrate yields macrotasks (setTimeout(0) chunking) — the mount can be
        // superseded MID-hydrate, after the dead check above already passed.
        // Re-check before arming beginRequest/startTimer (zombie interval).
        if (dead) return;
        if (running && inFlightReq) {
          // Re-enter the running state: request separator (unless the feed is
          // empty), replay the in-flight request's lines, resume the elapsed timer
          // from the manager's start timestamp.
          handle.beginRequest(inFlightReq.prompt, priorLines.length === 0);
          for (const line of inFlightLines) display({ event: 'line', data: { line } });
          startTimer(mgr.getStartedAt(projectDir) ?? Date.now());
        }
        refreshHistory();
      } finally {
        // ALWAYS leave the restoring state (review fix C1b): a throw above
        // would otherwise queue this panel's display forever.
        restoring = false;
        for (const queued of pendingWhileRestoring.splice(0)) display(queued);
      }
    })().catch(() => {});
  }
}

function deriveDisplayName(session: Session): string {
  const meta = (session.user.user_metadata ?? {}) as Record<string, unknown>;
  const full = (meta.full_name ?? meta.name) as string | undefined;
  if (full && typeof full === 'string' && full.trim()) return full.split(' ')[0]!;
  const email = session.user.email ?? '';
  return email.split('@')[0] ?? 'there';
}

// One slim 14-day progress bar per project (the free online window the operator
// wants kept). Each bar fills with the FRACTION of FREE_PREVIEW_DAYS still left
// for that idea (previewDaysLeft(createdAt) / FREE_PREVIEW_DAYS). Empty when the
// user has no projects yet.
export function renderProgressBars(projects: ProjectRow[]): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'welcome-progress__bars';
  wrap.setAttribute('data-component', 'welcome-progress-bars');
  for (const p of projects) {
    const left = previewDaysLeft(p.created_at);
    const pct = Math.round((left / FREE_PREVIEW_DAYS) * 100);
    const row = document.createElement('div');
    row.className = 'welcome-progress__row';
    row.innerHTML = `
      <span class="welcome-progress__label">${escapeHtml(p.title)}</span>
      <span class="welcome-progress__track"><i style="width:${pct}%"></i></span>
      <span class="welcome-progress__days">${left}/${FREE_PREVIEW_DAYS}j</span>`;
    wrap.appendChild(row);
  }
  return wrap;
}
